import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Activity,
  Battery,
  Building2,
  Droplet,
  Heart,
  Loader,
  Power,
  RefreshCw,
  SlidersHorizontal,
  Waves,
  Zap,
} from "lucide-react";
import mqtt from "mqtt";

const MQTT_BROKER_URL =
  process.env.REACT_APP_MQTT_BROKER_URL || "wss://aquatank.aquasense.tech:9001";
const MQTT_USERNAME = process.env.REACT_APP_MQTT_USERNAME || "";
const MQTT_PASSWORD = process.env.REACT_APP_MQTT_PASSWORD || "";
const MQTT_D2C_TOPIC = process.env.REACT_APP_MQTT_D2C_TOPIC || "AQUATANK/D2C/+";
const MQTT_STATE_TOPIC =
  process.env.REACT_APP_MQTT_STATE_TOPIC || "AQUATANK/STATE/+/+";
const MQTT_C2D_PREFIX = process.env.REACT_APP_MQTT_C2D_PREFIX || "AQUATANK/C2D";
const CONTROLLER_ACTIVE_WINDOW_MS = 5 * 60 * 1000;
const LIVE_STATUS_REFRESH_MS = 60 * 1000;
const MIN_COMMAND_PROCESSING_MS = 10 * 1000;
const PUMP_ACK_TIMEOUT_MS = 20 * 1000;
const VALVE_ACK_TIMEOUT_MS = 50 * 1000;

const modeOptions = ["manual", "auto", "remote"];
const ALL_DEVICES_VALUE = "__all_devices__";
const NO_TOWER_DASHBOARD_ID = "__no_tower__";
const DEVICE_LABEL_BY_TYPE = {
  level: "Valve Controller",
  pump: "Pump Controller",
  atbpc: "Borewell Pump Controller",
};

const normalize = (value) => String(value || "").toLowerCase();

const isUsableReference = (value) => {
  if (value === null || value === undefined) return false;

  const normalizedValue = String(value).trim().toLowerCase();
  return Boolean(
    normalizedValue &&
    !["-", "none", "null", "undefined"].includes(normalizedValue),
  );
};

const getUsableReference = (...values) => {
  const value = values.find(isUsableReference);
  return value === undefined ? "" : String(value).trim();
};

const formatControlTitle = (label, reference) =>
  reference ? `${label} (${reference})` : label;

const getControllerList = (tower, listKey, singleKey) => {
  if (Array.isArray(tower?.[listKey]) && tower[listKey].length > 0) {
    return tower[listKey];
  }
  return tower?.[singleKey] ? [tower[singleKey]] : [];
};

const getControllerByDeviceId = (tower, listKey, singleKey, deviceId) =>
  getControllerList(tower, listKey, singleKey).find(
    (controller) => controller?.device_id === deviceId,
  );

// Live packets sometimes carry "-" or leave a field out (e.g. ugt_level when
// the UGT sensor misses a read). Those must not wipe the last known reading,
// so only fields with a real value are applied over the previous status.
const withReportedValuesOnly = (status = {}) =>
  Object.fromEntries(
    Object.entries(status).filter(
      ([, value]) =>
        value !== null &&
        value !== undefined &&
        !(typeof value === "string" && ["", "-"].includes(value.trim())),
    ),
  );

const mergePumpStatuses = (previousStatuses, nextStatuses) => {
  const previousByPumpId = new Map(
    (Array.isArray(previousStatuses) ? previousStatuses : []).map((status) => [
      status?.pump_id,
      status,
    ]),
  );
  return nextStatuses.map((status) => ({
    ...(previousByPumpId.get(status?.pump_id) || {}),
    ...withReportedValuesOnly(status),
  }));
};

const getDeviceSelectionKey = (type, deviceId) => `${type}:${deviceId}`;
const getAllDevicesSelectionKey = (type) =>
  getDeviceSelectionKey(type, ALL_DEVICES_VALUE);

const getDeviceSelectionParts = (key) => {
  const [type, ...deviceIdParts] = String(key || "").split(":");
  const deviceId = deviceIdParts.join(":");
  return { type, deviceId };
};

const isAllDevicesSelection = (key) =>
  getDeviceSelectionParts(key).deviceId === ALL_DEVICES_VALUE;

const getDashboardFiltersForSelection = (key, selectedTower) => {
  const { type, deviceId } = getDeviceSelectionParts(key);

  if (deviceId === ALL_DEVICES_VALUE) {
    return {
      towerId: type === "level" ? selectedTower : null,
    };
  }

  if (!isUsableReference(deviceId)) return null;

  return {
    towerId: type === "level" ? selectedTower : null,
    deviceType: type,
    deviceId,
  };
};

const getDashboardFiltersSignature = (filters = {}) =>
  [filters.towerId, filters.deviceType, filters.deviceId]
    .map((value) => value || "")
    .join("|");

// A tower-scoped response only contains that tower, so merge it into the
// catalog instead of replacing it; otherwise the other towers lose their
// device options and switching to them never triggers a fetch.
const mergeTowerCatalogData = (currentCatalog, data, towerId) => {
  if (!towerId || !Array.isArray(currentCatalog?.data)) return data;

  const incomingTowers = Array.isArray(data?.data) ? data.data : [];
  const incomingByTowerId = new Map(
    incomingTowers.map((tower) => [tower.tower_id, tower]),
  );
  const mergedTowers = currentCatalog.data.map((tower) => {
    const incomingTower = incomingByTowerId.get(tower.tower_id);
    if (!incomingTower) return tower;
    incomingByTowerId.delete(tower.tower_id);
    return incomingTower;
  });

  return {
    ...currentCatalog,
    data: [...mergedTowers, ...incomingByTowerId.values()],
  };
};

const buildDeviceOptions = (towers, selectedDeviceType) => {
  const listKeyByType = {
    level: "level_controllers",
    pump: "pump_controllers",
    atbpc: "atbpc_controllers",
  };
  const singleKeyByType = {
    level: "level_controller",
    pump: "pump_controller",
    atbpc: "atbpc_controller",
  };
  const seen = new Set();

  return (towers || [])
    .flatMap((tower) =>
      getControllerList(
        tower,
        listKeyByType[selectedDeviceType],
        singleKeyByType[selectedDeviceType],
      )
        .filter((controller) => isUsableReference(controller?.device_id))
        .map((controller) => ({
          key: getDeviceSelectionKey(selectedDeviceType, controller.device_id),
          type: selectedDeviceType,
          deviceId: controller.device_id,
          label: `${controller.device_id} - ${DEVICE_LABEL_BY_TYPE[selectedDeviceType]}`,
          tower,
          controller,
        })),
    )
    .filter((option) => {
      if (seen.has(option.key)) return false;
      seen.add(option.key);
      return true;
    });
};

// Valve controller cards: one per row on mobiles and tablets, two per row
// from laptop width up. All rows share the tallest card's height; shorter
// cards fill the gap with white space above the footer.
const LEVEL_CARD_GRID_CLASS =
  "grid grid-cols-1 auto-rows-fr gap-4 lg:grid-cols-2";

const getDeviceCardCount = (device) => {
  if (device.type === "pump") {
    return Math.max(
      1,
      getPumpControllerStatuses(device.tower, device.controller).length,
    );
  }

  if (device.type === "atbpc") {
    return Math.max(1, getATBPCControllerStatuses(device.controller).length);
  }

  return 1;
};

const mergeDeviceOptionWithCatalog = (visibleOption, catalogOption) => {
  if (!visibleOption || !catalogOption) return visibleOption || catalogOption;

  return {
    ...visibleOption,
    tower: {
      ...catalogOption.tower,
      ...visibleOption.tower,
      tanks:
        visibleOption.tower?.tanks?.length > 0
          ? visibleOption.tower.tanks
          : catalogOption.tower?.tanks || [],
    },
  };
};

const buildTowerCardsWithExtraData = (towers, data) => {
  const dataByTowerId = new Map(data.map((tower) => [tower.tower_id, tower]));
  const knownTowerIds = new Set((towers || []).map((tower) => tower.id));
  const towerCards = (towers || []).map((tower) => ({
    ...dataByTowerId.get(tower.id),
    tower_id: tower.id,
    tower_name: dataByTowerId.get(tower.id)?.tower_name || tower.name,
    tower_location:
      dataByTowerId.get(tower.id)?.tower_location || tower.location,
    is_active: Boolean(tower.active),
    tanks: dataByTowerId.get(tower.id)?.tanks || [],
  }));
  const extraDataCards = data
    .filter((tower) => !knownTowerIds.has(tower.tower_id))
    .map((tower) => ({ ...tower, is_active: tower.tower_active ?? true }));

  return [...towerCards, ...extraDataCards];
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "-";
  const timestampMs = getTimestampMs(timestamp);
  const date = timestampMs ? new Date(timestampMs) : new Date(timestamp);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getTimestampMs = (timestamp) => {
  if (!timestamp) return null;

  if (typeof timestamp === "number") {
    const timestampMs = timestamp * 1000;
    const now = Date.now();

    if (timestampMs - now > 5 * 60 * 1000) {
      return timestampMs - 5.5 * 60 * 60 * 1000;
    }

    return timestampMs;
  }

  const timestampMs = new Date(timestamp).getTime();
  return Number.isNaN(timestampMs) ? null : timestampMs;
};

const isControllerActive = (timestamp, nowMs) => {
  const timestampMs = getTimestampMs(timestamp);
  return Boolean(
    timestampMs &&
    nowMs - timestampMs >= 0 &&
    nowMs - timestampMs <= CONTROLLER_ACTIVE_WINDOW_MS,
  );
};

const isOn = (value) => ["on", "open", "true", "1"].includes(normalize(value));

const formatWholePercent = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.round(numericValue) : "-";
};

const formatUgtLevel = (value) => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${Math.round(numericValue)}%` : value;
};

const formatOptionalPercent = (value) => {
  if (!isUsableReference(value)) return "-";
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${Math.round(numericValue)}%` : value;
};

const isBorewellSourceType = (type) =>
  type === 0 || type === "0" || normalize(type) === "borewell";

const formatATBPCSourceLevel = (value, sourceType) => {
  if (!isUsableReference(value)) return "-";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;
  return isBorewellSourceType(sourceType)
    ? `${numericValue.toFixed(2).replace(/\.?0+$/, "")} m`
    : `${Math.round(numericValue)}%`;
};

const isStateTopic = (topic) => topic.startsWith("AQUATANK/STATE/");

const getHealthText = (health) => {
  if (!health) return "Unknown";
  return health === "no_fault" ? "Healthy" : health.replaceAll("_", " ");
};

const buildCommandPayload = ({
  deviceType,
  deviceId,
  command,
  mode,
  remoteMode,
  targetId,
  targetType,
  targetState,
}) => ({
  command_id: `${deviceId}-${Date.now()}`,
  device_id: deviceId,
  device_type: deviceType,
  config_type: "control",
  command,
  operation_mode: mode,
  remote_mode: remoteMode,
  target_type: targetType,
  target_id: targetId,
  target_state: targetState,
  timestamp: Math.floor(Date.now() / 1000),
});

const buildValveTogglePayload = ({ deviceId, valveId, operation }) => ({
  device_id: deviceId,
  timestamp: Math.floor(Date.now() / 1000),
  mid: `${Date.now()}${Math.random().toString(36).slice(2, 8)}`,
  valve_id: valveId,
  operation,
});

const buildPumpTogglePayload = ({ deviceId, pumpId, operation }) => ({
  device_id: deviceId,
  timestamp: Math.floor(Date.now() / 1000),
  mid: `${Date.now()}${Math.random().toString(36).slice(2, 8)}`,
  pump_id: pumpId,
  operation,
});

const buildRemoteOperationsPayload = ({ deviceId, remoteOperations }) => ({
  device_id: deviceId,
  timestamp: Math.floor(Date.now() / 1000),
  mid: `${Date.now()}${Math.random().toString(36).slice(2, 8)}`,
  remote_operations: remoteOperations,
});

const Dashboard = () => {
  const { isSuperAdmin } = useAuth();
  const isSuperAdminUser = isSuperAdmin();
  const mqttClientRef = useRef(null);
  const pendingCommandTimersRef = useRef({});
  const pendingCommandsRef = useRef({});
  const statePacketQueueRef = useRef([]);
  const stateFlushTimerRef = useRef(null);
  const latestDashboardRequestRef = useRef(0);
  const lastRequestedFiltersRef = useRef(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [deviceCatalogData, setDeviceCatalogData] = useState(null);
  const [loading, setLoading] = useState(true);
  // null | "filter" (selection changed, hide stale data) | "refresh"
  const [dataRequestKind, setDataRequestKind] = useState(null);
  const [selectedDeviceType, setSelectedDeviceType] = useState("level");
  const [selectedTower, setSelectedTower] = useState(null);
  const [selectedDeviceKey, setSelectedDeviceKey] = useState("");
  const [allTowers, setAllTowers] = useState([]);
  const [mqttConnected, setMqttConnected] = useState(false);
  const [pendingCommands, setPendingCommands] = useState({});
  const [statusCheckTime, setStatusCheckTime] = useState(Date.now());

  useEffect(() => {
    pendingCommandsRef.current = pendingCommands;
  }, [pendingCommands]);

  const applyDevicePackets = useCallback((packets) => {
    const latestPackets = Array.from(
      packets
        .filter((packet) => packet?.device_id)
        .filter((packet) => {
          const packetTimestampMs = getTimestampMs(packet.timestamp);
          if (!packetTimestampMs) return true;

          return !Object.values(pendingCommandsRef.current).some(
            (command) =>
              command.deviceId === packet.device_id &&
              command.startedAt &&
              packetTimestampMs < command.startedAt,
          );
        })
        .reduce((map, packet) => {
          map.set(`${packet.device_type}:${packet.device_id}`, packet);
          return map;
        }, new Map())
        .values(),
    );

    if (latestPackets.length === 0) return;

    setStatusCheckTime(Date.now());

    setDashboardData((current) => {
      if (!current?.data) return current;

      let changed = false;
      let nextData = current.data;

      latestPackets.forEach((packet) => {
        nextData = nextData.map((tower) => {
          if (packet.device_type === "v_controller") {
            const levelControllers = getControllerList(
              tower,
              "level_controllers",
              "level_controller",
            );
            const isSameLevelController =
              levelControllers.some(
                (controller) => controller?.device_id === packet.device_id,
              ) ||
              tower.tanks?.some(
                (tank) =>
                  (tank.level_controller_device_id ||
                    tower.level_controller?.device_id) === packet.device_id,
              );

            if (!isSameLevelController) return tower;

            const statuses = Array.isArray(packet.status) ? packet.status : [];
            changed = true;

            return {
              ...tower,
              level_controllers: levelControllers.map((controller) =>
                controller.device_id === packet.device_id
                  ? {
                      ...controller,
                      device_id: packet.device_id,
                      battery_level: packet.battery_level,
                      operation_mode: packet.operation_mode,
                      remote_mode: packet.remote_mode,
                      timestamp: packet.timestamp,
                      status: statuses,
                    }
                  : controller,
              ),
              level_controller: {
                ...(tower.level_controller || {}),
                device_id: packet.device_id,
                battery_level: packet.battery_level,
                operation_mode: packet.operation_mode,
                remote_mode: packet.remote_mode,
                timestamp: packet.timestamp,
                status: statuses,
              },
              tanks: (tower.tanks || []).map((tank) => {
                const isTankForController =
                  (tank.level_controller_device_id ||
                    tower.level_controller?.device_id) === packet.device_id;
                if (!isTankForController) return tank;

                // Match level by tank; a valve may be shared across tanks, so
                // a valve-only match supplies valve state but not level.
                const tankStatus = statuses.find(
                  (item) => item.tank_id === tank.tank_id,
                );
                const status =
                  tankStatus ||
                  (isUsableReference(tank.valve_id)
                    ? statuses.find((item) => item.valve_id === tank.valve_id)
                    : undefined);

                if (!status) {
                  return {
                    ...tank,
                    level_controller_mode:
                      packet.operation_mode || tank.level_controller_mode,
                    level_controller_remote:
                      packet.remote_mode || tank.level_controller_remote,
                    level_controller_battery:
                      packet.battery_level ?? tank.level_controller_battery,
                  };
                }

                return {
                  ...tank,
                  tank_percentage: tankStatus
                    ? (tankStatus.tank_percentage ?? tank.tank_percentage)
                    : tank.tank_percentage,
                  has_live_data: tankStatus ? true : tank.has_live_data,
                  valve_state: status.valve_state || tank.valve_state,
                  require_water: packet.require_water ?? tank.require_water,
                  level_controller_mode:
                    packet.operation_mode || tank.level_controller_mode,
                  level_controller_remote:
                    packet.remote_mode || tank.level_controller_remote,
                  level_controller_battery:
                    packet.battery_level ?? tank.level_controller_battery,
                };
              }),
            };
          }

          if (packet.device_type === "p_controller") {
            const pumpControllers = getControllerList(
              tower,
              "pump_controllers",
              "pump_controller",
            );
            const isSamePumpController =
              pumpControllers.some(
                (controller) => controller?.device_id === packet.device_id,
              ) ||
              tower.tanks?.some(
                (tank) =>
                  (tank.pump_controller_device_id ||
                    tower.pump_controller?.device_id) === packet.device_id,
              );

            if (!isSamePumpController) return tower;

            const statuses = Array.isArray(packet.status)
              ? packet.status
              : packet.pump_id
                ? [packet]
                : [];
            changed = true;

            return {
              ...tower,
              pump_controllers: pumpControllers.map((controller) =>
                controller.device_id === packet.device_id
                  ? {
                      ...controller,
                      device_id: packet.device_id,
                      battery_level:
                        packet.battery_level ?? controller.battery_level,
                      operation_mode: packet.operation_mode,
                      remote_mode: packet.remote_mode,
                      timestamp: packet.timestamp,
                      status: mergePumpStatuses(controller.status, statuses),
                    }
                  : controller,
              ),
              pump_controller: {
                ...(tower.pump_controller || {}),
                device_id: packet.device_id,
                battery_level:
                  packet.battery_level ?? tower.pump_controller?.battery_level,
                operation_mode: packet.operation_mode,
                remote_mode: packet.remote_mode,
                timestamp: packet.timestamp,
                status: mergePumpStatuses(tower.pump_controller?.status, statuses),
              },
              tanks: (tower.tanks || []).map((tank) => {
                const isTankForController =
                  (tank.pump_controller_device_id ||
                    tower.pump_controller?.device_id) === packet.device_id;
                if (!isTankForController) return tank;

                const pumpId =
                  tank.pump_packet?.pump_id ||
                  tank.pump?.pump_custom_id ||
                  tank.pump?.pump_id;
                const status = statuses.find((item) => item.pump_id === pumpId);

                return {
                  ...tank,
                  pump_packet: status
                    ? {
                        ...(tank.pump_packet || {}),
                        ...withReportedValuesOnly(status),
                        atbc_mode: packet.operation_mode,
                        atbc_remote: packet.remote_mode,
                        atbc_timestamp: packet.timestamp,
                      }
                    : tank.pump_packet,
                };
              }),
            };
          }

          if (packet.device_type === "b_controller") {
            const atbpcControllers = getControllerList(
              tower,
              "atbpc_controllers",
              "atbpc_controller",
            );
            const isSameATBPCController = atbpcControllers.some(
              (controller) => controller?.device_id === packet.device_id,
            );

            if (!isSameATBPCController) return tower;

            const statuses = Array.isArray(packet.status)
              ? packet.status
              : packet.pump_id
                ? [packet]
                : [];
            const statusesWithControllerState = statuses.map((status) => ({
              ...status,
              atbpc_mode: packet.operation_mode,
              atbpc_remote: packet.remote_mode,
              atbpc_timestamp: packet.timestamp,
            }));
            changed = true;

            return {
              ...tower,
              atbpc_controllers: atbpcControllers.map((controller) =>
                controller.device_id === packet.device_id
                  ? {
                      ...controller,
                      device_id: packet.device_id,
                      battery_level:
                        packet.battery_level ?? controller.battery_level,
                      operation_mode: packet.operation_mode,
                      remote_mode: packet.remote_mode,
                      timestamp: packet.timestamp,
                      status: statusesWithControllerState,
                    }
                  : controller,
              ),
              atbpc_controller: {
                ...(tower.atbpc_controller || {}),
                device_id: packet.device_id,
                battery_level:
                  packet.battery_level ?? tower.atbpc_controller?.battery_level,
                operation_mode: packet.operation_mode,
                remote_mode: packet.remote_mode,
                timestamp: packet.timestamp,
                status: statusesWithControllerState,
              },
            };
          }

          return tower;
        });

        // A pump can supply tanks in other towers; their names/levels live in
        // the pump controller's connected_tanks. Keep those levels live too.
        if (packet.device_type === "v_controller") {
          const levelByTankId = new Map(
            (Array.isArray(packet.status) ? packet.status : [])
              .filter(
                (item) =>
                  isUsableReference(item?.tank_id) &&
                  item.tank_percentage !== undefined &&
                  item.tank_percentage !== null &&
                  item.tank_percentage !== "-",
              )
              .map((item) => [item.tank_id, item.tank_percentage]),
          );
          if (levelByTankId.size === 0) return;

          const updateConnectedTanks = (controller) => {
            const connectedTanks = controller?.connected_tanks;
            if (!connectedTanks) return controller;
            let touched = false;
            const nextConnected = { ...connectedTanks };
            levelByTankId.forEach((level, tankId) => {
              if (nextConnected[tankId]) {
                nextConnected[tankId] = {
                  ...nextConnected[tankId],
                  tank_percentage: level,
                };
                touched = true;
              }
            });
            if (!touched) return controller;
            changed = true;
            return { ...controller, connected_tanks: nextConnected };
          };

          nextData = nextData.map((tower) => ({
            ...tower,
            pump_controllers: getControllerList(
              tower,
              "pump_controllers",
              "pump_controller",
            ).map(updateConnectedTanks),
            pump_controller: tower.pump_controller
              ? updateConnectedTanks(tower.pump_controller)
              : tower.pump_controller,
          }));
        }
      });

      if (!changed) return current;
      return {
        ...current,
        data: nextData,
        timestamp: new Date().toISOString(),
      };
    });
  }, []);

  const flushStatePackets = useCallback(() => {
    stateFlushTimerRef.current = null;
    const packets = statePacketQueueRef.current;
    statePacketQueueRef.current = [];
    applyDevicePackets(packets);
  }, [applyDevicePackets]);

  const enqueueStatePacket = useCallback(
    (packet) => {
      statePacketQueueRef.current.push(packet);

      if (!stateFlushTimerRef.current) {
        stateFlushTimerRef.current = setTimeout(flushStatePackets, 150);
      }
    },
    [flushStatePackets],
  );

  const isSuccessfulAck = (ack) =>
    ["pass", "success"].includes(normalize(ack?.result));

  const completePendingCommand = useCallback(
    (commandId, { toastMessage, toastType = "success" } = {}) => {
      const pendingCommand = pendingCommandsRef.current[commandId];
      const elapsedMs = pendingCommand?.startedAt
        ? Date.now() - pendingCommand.startedAt
        : MIN_COMMAND_PROCESSING_MS;
      const remainingMs = Math.max(0, MIN_COMMAND_PROCESSING_MS - elapsedMs);

      if (pendingCommandTimersRef.current[commandId]) {
        clearTimeout(pendingCommandTimersRef.current[commandId]);
        delete pendingCommandTimersRef.current[commandId];
      }

      const finish = () => {
        const refNext = { ...pendingCommandsRef.current };
        delete refNext[commandId];
        pendingCommandsRef.current = refNext;

        setPendingCommands((current) => {
          const next = { ...current };
          delete next[commandId];
          return next;
        });

        if (toastMessage) {
          toastType === "error"
            ? toast.error(toastMessage)
            : toast.success(toastMessage);
        }
      };

      if (remainingMs > 0) {
        pendingCommandTimersRef.current[commandId] = setTimeout(
          finish,
          remainingMs,
        );
      } else {
        finish();
      }
    },
    [],
  );

  const applyToggleAcknowledgement = useCallback((ack, pendingCommand) => {
    if (!pendingCommand || !isSuccessfulAck(ack)) return false;

    console.log("acknowledgement received:", ack, pendingCommand);

    if (pendingCommand.type === "remote") {
      if (
        ack.device_id !== pendingCommand.deviceId ||
        ack.remote_operations !== pendingCommand.remoteOperations
      ) {
        return false;
      }

      setDashboardData((current) => {
        if (!current?.data) return current;

        const nextData = current.data.map((tower) => {
          if (pendingCommand.controllerType === "level") {
            const levelControllers = getControllerList(
              tower,
              "level_controllers",
              "level_controller",
            );
            const isMatchingLevelController =
              levelControllers.some(
                (controller) =>
                  controller?.device_id === pendingCommand.deviceId,
              ) ||
              tower.tanks?.some(
                (tank) =>
                  (tank.level_controller_device_id ||
                    tower.level_controller?.device_id) ===
                  pendingCommand.deviceId,
              );

            if (!isMatchingLevelController) return tower;

            return {
              ...tower,
              level_controllers: levelControllers.map((controller) =>
                controller.device_id === pendingCommand.deviceId
                  ? {
                      ...controller,
                      remote_mode: pendingCommand.remoteOperations,
                    }
                  : controller,
              ),
              level_controller: {
                ...(tower.level_controller || {}),
                remote_mode: pendingCommand.remoteOperations,
              },
              tanks: (tower.tanks || []).map((tank) =>
                (tank.level_controller_device_id ||
                  tower.level_controller?.device_id) === pendingCommand.deviceId
                  ? {
                      ...tank,
                      level_controller_remote: pendingCommand.remoteOperations,
                    }
                  : tank,
              ),
            };
          }

          if (pendingCommand.controllerType === "pump") {
            const pumpControllers = getControllerList(
              tower,
              "pump_controllers",
              "pump_controller",
            );
            const isMatchingPumpController =
              pumpControllers.some(
                (controller) =>
                  controller?.device_id === pendingCommand.deviceId,
              ) ||
              tower.tanks?.some(
                (tank) =>
                  (tank.pump_controller_device_id ||
                    tower.pump_controller?.device_id) ===
                  pendingCommand.deviceId,
              );

            if (!isMatchingPumpController) return tower;

            return {
              ...tower,
              pump_controllers: pumpControllers.map((controller) =>
                controller.device_id === pendingCommand.deviceId
                  ? {
                      ...controller,
                      remote_mode: pendingCommand.remoteOperations,
                    }
                  : controller,
              ),
              pump_controller: {
                ...(tower.pump_controller || {}),
                remote_mode: pendingCommand.remoteOperations,
              },
              tanks: (tower.tanks || []).map((tank) =>
                (tank.pump_controller_device_id ||
                  tower.pump_controller?.device_id) === pendingCommand.deviceId
                  ? {
                      ...tank,
                      pump_packet: {
                        ...(tank.pump_packet || {}),
                        atbc_remote: pendingCommand.remoteOperations,
                      },
                    }
                  : tank,
              ),
            };
          }

          if (pendingCommand.controllerType === "atbpc") {
            const atbpcControllers = getControllerList(
              tower,
              "atbpc_controllers",
              "atbpc_controller",
            );
            const isMatchingATBPCController = atbpcControllers.some(
              (controller) => controller?.device_id === pendingCommand.deviceId,
            );

            if (!isMatchingATBPCController) return tower;

            return {
              ...tower,
              atbpc_controllers: atbpcControllers.map((controller) =>
                controller.device_id === pendingCommand.deviceId
                  ? {
                      ...controller,
                      remote_mode: pendingCommand.remoteOperations,
                      status: (controller.status || []).map((status) => ({
                        ...status,
                        atbpc_remote: pendingCommand.remoteOperations,
                      })),
                    }
                  : controller,
              ),
              atbpc_controller: {
                ...(tower.atbpc_controller || {}),
                remote_mode: pendingCommand.remoteOperations,
                status: (tower.atbpc_controller?.status || []).map(
                  (status) => ({
                    ...status,
                    atbpc_remote: pendingCommand.remoteOperations,
                  }),
                ),
              },
            };
          }

          return tower;
        });

        return {
          ...current,
          data: nextData,
          timestamp: new Date().toISOString(),
        };
      });

      return true;
    }

    const isValidBaseAck =
      ack.device_id === pendingCommand.deviceId &&
      ack.operation === pendingCommand.operation;

    if (!isValidBaseAck) return false;

    if (
      pendingCommand.type === "valve" &&
      ack.valve_id !== pendingCommand.targetId
    ) {
      return false;
    }

    if (
      pendingCommand.type === "pump" &&
      ack.pump_id !== pendingCommand.targetId
    ) {
      return false;
    }

    setDashboardData((current) => {
      if (!current?.data) return current;

      const nextData = current.data.map((tower) => ({
        ...tower,
        atbpc_controllers: getControllerList(
          tower,
          "atbpc_controllers",
          "atbpc_controller",
        ).map((controller) =>
          pendingCommand.type === "pump" &&
          controller.device_id === pendingCommand.deviceId
            ? {
                ...controller,
                status: (controller.status || []).map((status) =>
                  status.pump_id === pendingCommand.targetId
                    ? {
                        ...status,
                        pump_state:
                          pendingCommand.operation === "on" ? "ON" : "OFF",
                      }
                    : status,
                ),
              }
            : controller,
        ),
        atbpc_controller:
          pendingCommand.type === "pump" &&
          tower.atbpc_controller?.device_id === pendingCommand.deviceId
            ? {
                ...tower.atbpc_controller,
                status: (tower.atbpc_controller.status || []).map((status) =>
                  status.pump_id === pendingCommand.targetId
                    ? {
                        ...status,
                        pump_state:
                          pendingCommand.operation === "on" ? "ON" : "OFF",
                      }
                    : status,
                ),
              }
            : tower.atbpc_controller,
        tanks: (tower.tanks || []).map((tank) => {
          if (
            pendingCommand.type === "valve" &&
            (tank.level_controller_device_id ||
              tower.level_controller?.device_id) === pendingCommand.deviceId &&
            tank.valve_id === pendingCommand.targetId
          ) {
            return {
              ...tank,
              valve_state:
                pendingCommand.operation === "open" ? "OPEN" : "CLOSED",
            };
          }

          const pumpId =
            tank.pump_packet?.pump_id ||
            tank.pump?.pump_custom_id ||
            tank.pump?.pump_id;

          if (
            pendingCommand.type === "pump" &&
            (tank.pump_controller_device_id ||
              tower.pump_controller?.device_id) === pendingCommand.deviceId &&
            pumpId === pendingCommand.targetId
          ) {
            return {
              ...tank,
              pump_packet: {
                ...(tank.pump_packet || {}),
                pump_id: pendingCommand.targetId,
                pump_state: pendingCommand.operation === "on" ? "ON" : "OFF",
              },
            };
          }

          return tank;
        }),
      }));

      return {
        ...current,
        data: nextData,
        timestamp: new Date().toISOString(),
      };
    });

    return true;
  }, []);

  const clearPendingCommandImmediately = useCallback((commandId) => {
    if (pendingCommandTimersRef.current[commandId]) {
      clearTimeout(pendingCommandTimersRef.current[commandId]);
      delete pendingCommandTimersRef.current[commandId];
    }

    const refNext = { ...pendingCommandsRef.current };
    delete refNext[commandId];
    pendingCommandsRef.current = refNext;

    setPendingCommands((current) => {
      const next = { ...current };
      delete next[commandId];
      return next;
    });
  }, []);

  const handleMqttMessage = useCallback(
    (topic, message) => {
      try {
        const payload = JSON.parse(message.toString());

        if (isStateTopic(topic)) {
          enqueueStatePacket(payload);
          return;
        }

        if (payload.mid && payload.result) {
          const ackId = payload.mid;
          const pendingCommand = pendingCommandsRef.current[ackId];

          if (pendingCommand) {
            const isValidAck = applyToggleAcknowledgement(
              payload,
              pendingCommand,
            );
            const ackPassed = isSuccessfulAck(payload);

            completePendingCommand(ackId, {
              toastMessage:
                isValidAck && ackPassed
                  ? "Device acknowledged command"
                  : ackPassed
                    ? "Invalid acknowledgement received"
                    : "Device rejected command",
              toastType: isValidAck && ackPassed ? "success" : "error",
            });
          }
          return;
        }

        if (payload.command_id) {
          completePendingCommand(payload.command_id, {
            toastMessage: "Device acknowledged command",
          });
        }

        if (
          payload.command_result ||
          payload.control_result ||
          payload.set_control_result
        ) {
          const result =
            payload.command_result ||
            payload.control_result ||
            payload.set_control_result;
          if (result === "pass" || result === "success") {
            toast.success("Device acknowledged command");
          } else {
            toast.error("Device rejected command");
          }
        }

        applyDevicePackets([payload]);
      } catch (error) {
        console.error("Invalid MQTT message", topic, error);
      }
    },
    [
      applyDevicePackets,
      applyToggleAcknowledgement,
      completePendingCommand,
      enqueueStatePacket,
    ],
  );

  useEffect(() => {
    const client = mqtt.connect(MQTT_BROKER_URL, {
      username: MQTT_USERNAME,
      password: MQTT_PASSWORD,
      reconnectPeriod: 2000,
      clean: true,
    });

    mqttClientRef.current = client;

    client.on("connect", () => {
      setMqttConnected(true);
      client.subscribe([MQTT_D2C_TOPIC, MQTT_STATE_TOPIC], (error) => {
        if (error) {
          toast.error("Failed to subscribe to MQTT updates");
        }
      });
    });

    client.on("message", handleMqttMessage);
    client.on("reconnect", () => setMqttConnected(false));
    client.on("close", () => setMqttConnected(false));
    client.on("error", (error) => {
      console.error("MQTT error:", error);
      setMqttConnected(false);
    });

    return () => {
      Object.values(pendingCommandTimersRef.current).forEach(clearTimeout);
      pendingCommandTimersRef.current = {};
      if (stateFlushTimerRef.current) {
        clearTimeout(stateFlushTimerRef.current);
        stateFlushTimerRef.current = null;
      }
      statePacketQueueRef.current = [];
      client.end(true);
      mqttClientRef.current = null;
    };
  }, [handleMqttMessage]);

  const loadDashboardData = useCallback(
    async (showToast = false, filters = {}) => {
      const requestId = latestDashboardRequestRef.current + 1;
      latestDashboardRequestRef.current = requestId;
      const isLatestRequest = () =>
        latestDashboardRequestRef.current === requestId;

      lastRequestedFiltersRef.current = getDashboardFiltersSignature(filters);
      setDataRequestKind(showToast ? "refresh" : "filter");

      try {
        const data = await api.getDashboardData(filters);
        // A newer selection was made while this request was in flight.
        if (!isLatestRequest()) return;

        setDashboardData(data);
        setStatusCheckTime(Date.now());

        if (!filters.deviceId) {
          setDeviceCatalogData((currentCatalog) =>
            mergeTowerCatalogData(currentCatalog, data, filters.towerId),
          );
        }

        if (data.towers && isSuperAdminUser) {
          setAllTowers((currentTowers) => {
            if (!filters.towerId) {
              return data.towers;
            }

            return currentTowers.length > 0 ? currentTowers : data.towers;
          });
        }

        if (showToast) {
          toast.success("Dashboard refreshed");
        }
      } catch (error) {
        if (!isLatestRequest()) return;
        // Allow the same selection to be fetched again after a failure.
        lastRequestedFiltersRef.current = null;
        console.error("Error loading dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        if (isLatestRequest()) {
          setLoading(false);
          setDataRequestKind(null);
        }
      }
    },
    [isSuperAdminUser],
  );

  useEffect(() => {
    setLoading(true);
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setStatusCheckTime(Date.now());
    }, LIVE_STATUS_REFRESH_MS);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    // While the device key is reset (e.g. right after switching towers) fetch
    // the whole selection, so a tower with no cached devices still loads.
    const filters = getDashboardFiltersForSelection(
      selectedDeviceKey || getAllDevicesSelectionKey(selectedDeviceType),
      selectedTower,
    );
    if (!filters) return;

    // Skip when these exact filters are already loaded or in flight.
    if (
      lastRequestedFiltersRef.current === getDashboardFiltersSignature(filters)
    ) {
      return;
    }

    loadDashboardData(false, filters);
  }, [loadDashboardData, selectedDeviceKey, selectedDeviceType, selectedTower]);

  const publishCommand = ({
    deviceId,
    deviceType,
    command,
    mode,
    remoteMode,
    targetId,
    targetType,
    targetState,
  }) => {
    const client = mqttClientRef.current;

    if (!client?.connected) {
      toast.error("MQTT is not connected");
      return;
    }

    const payload = buildCommandPayload({
      deviceType,
      deviceId,
      command,
      mode,
      remoteMode,
      targetId,
      targetType,
      targetState,
    });

    setPendingCommands((current) => ({
      ...current,
      [payload.command_id]: true,
    }));

    client.publish(
      `${MQTT_C2D_PREFIX}/${deviceId}`,
      JSON.stringify(payload),
      { qos: 1 },
      (error) => {
        if (error) {
          setPendingCommands((current) => {
            const next = { ...current };
            delete next[payload.command_id];
            return next;
          });
          toast.error("Failed to send MQTT command");
        } else {
          toast.success("Command sent, waiting for acknowledgement");
        }
      },
    );
  };

  const publishValveToggleCommand = ({
    societyId,
    deviceId,
    valveId,
    operation,
  }) => {
    const client = mqttClientRef.current;

    if (!client?.connected) {
      toast.error("MQTT is not connected");
      return;
    }

    if (
      !societyId ||
      !isUsableReference(deviceId) ||
      !isUsableReference(valveId)
    ) {
      toast.error("Missing valve command details");
      return;
    }

    const payload = buildValveTogglePayload({
      deviceId,
      valveId,
      operation,
    });

    const pendingCommand = {
      type: "valve",
      societyId,
      deviceId,
      targetId: valveId,
      operation,
      startedAt: Date.now(),
    };

    setPendingCommands((current) => ({
      ...current,
      [payload.mid]: pendingCommand,
    }));
    pendingCommandsRef.current = {
      ...pendingCommandsRef.current,
      [payload.mid]: pendingCommand,
    };

    pendingCommandTimersRef.current[payload.mid] = setTimeout(() => {
      completePendingCommand(payload.mid, {
        toastMessage: "Valve acknowledgement timed out",
        toastType: "error",
      });
    }, VALVE_ACK_TIMEOUT_MS);

    client.publish(
      `${MQTT_C2D_PREFIX}/${societyId}`,
      JSON.stringify(payload),
      { qos: 1 },
      (error) => {
        if (error) {
          clearPendingCommandImmediately(payload.mid);
          toast.error("Failed to send valve command");
        } else {
          toast.success("Valve command sent, waiting for acknowledgement");
        }
      },
    );
  };

  const publishPumpToggleCommand = ({
    societyId,
    deviceId,
    pumpId,
    operation,
  }) => {
    const client = mqttClientRef.current;

    if (!client?.connected) {
      toast.error("MQTT is not connected");
      return;
    }

    if (
      !societyId ||
      !isUsableReference(deviceId) ||
      !isUsableReference(pumpId)
    ) {
      toast.error("Missing pump command details");
      return;
    }

    const payload = buildPumpTogglePayload({
      deviceId,
      pumpId,
      operation,
    });

    const pendingCommand = {
      type: "pump",
      societyId,
      deviceId,
      targetId: pumpId,
      operation,
      startedAt: Date.now(),
    };

    setPendingCommands((current) => ({
      ...current,
      [payload.mid]: pendingCommand,
    }));
    pendingCommandsRef.current = {
      ...pendingCommandsRef.current,
      [payload.mid]: pendingCommand,
    };

    pendingCommandTimersRef.current[payload.mid] = setTimeout(() => {
      completePendingCommand(payload.mid, {
        toastMessage: "Pump acknowledgement timed out",
        toastType: "error",
      });
    }, PUMP_ACK_TIMEOUT_MS);

    client.publish(
      `${MQTT_C2D_PREFIX}/${societyId}`,
      JSON.stringify(payload),
      { qos: 1 },
      (error) => {
        if (error) {
          clearPendingCommandImmediately(payload.mid);
          toast.error("Failed to send pump command");
        } else {
          toast.success("Pump command sent, waiting for acknowledgement");
        }
      },
    );
  };

  const publishRemoteOperationCommand = ({
    societyId,
    deviceId,
    controllerType,
    remoteOperations,
  }) => {
    const client = mqttClientRef.current;

    if (!client?.connected) {
      toast.error("MQTT is not connected");
      return;
    }

    if (!societyId || !isUsableReference(deviceId)) {
      toast.error("Missing remote operation details");
      return;
    }

    const payload = buildRemoteOperationsPayload({
      deviceId,
      remoteOperations,
    });

    const pendingCommand = {
      type: "remote",
      societyId,
      deviceId,
      controllerType,
      remoteOperations,
      startedAt: Date.now(),
    };

    setPendingCommands((current) => ({
      ...current,
      [payload.mid]: pendingCommand,
    }));
    pendingCommandsRef.current = {
      ...pendingCommandsRef.current,
      [payload.mid]: pendingCommand,
    };

    pendingCommandTimersRef.current[payload.mid] = setTimeout(
      () => {
        completePendingCommand(payload.mid, {
          toastMessage: "Remote operation acknowledgement timed out",
          toastType: "error",
        });
      },
      ["pump", "atbpc"].includes(controllerType)
        ? PUMP_ACK_TIMEOUT_MS
        : VALVE_ACK_TIMEOUT_MS,
    );

    client.publish(
      `${MQTT_C2D_PREFIX}/${societyId}`,
      JSON.stringify(payload),
      { qos: 1 },
      (error) => {
        if (error) {
          clearPendingCommandImmediately(payload.mid);
          toast.error("Failed to send remote operation");
        } else {
          toast.success("Remote operation sent, waiting for acknowledgement");
        }
      },
    );
  };

  const towerCards = useMemo(() => {
    const data = Array.isArray(dashboardData?.data) ? dashboardData.data : [];
    const dataByTowerId = new Map(data.map((tower) => [tower.tower_id, tower]));

    if (selectedTower) {
      const selectedTowerMeta = allTowers.find(
        (tower) => tower.id === selectedTower,
      );
      const selectedTowerData =
        dataByTowerId.get(selectedTower) ||
        data.find((tower) => tower.tower_id === selectedTower);

      if (selectedTowerData) {
        return [
          {
            ...selectedTowerData,
            tower_id: selectedTower,
            tower_name:
              selectedTowerData.tower_name || selectedTowerMeta?.name || "-",
            tower_location:
              selectedTowerData.tower_location ||
              selectedTowerMeta?.location ||
              "-",
            is_active: selectedTowerMeta?.active ?? true,
            tanks: selectedTowerData.tanks || [],
          },
        ];
      }

      if (selectedTowerMeta) {
        return [
          {
            tower_id: selectedTowerMeta.id,
            tower_name: selectedTowerMeta.name,
            tower_location: selectedTowerMeta.location,
            is_active: Boolean(selectedTowerMeta.active),
            tanks: [],
          },
        ];
      }

      return [];
    }

    if (isSuperAdminUser && allTowers.length > 0) {
      return buildTowerCardsWithExtraData(allTowers, data);
    }

    return data.map((tower) => ({ ...tower, is_active: true }));
  }, [allTowers, dashboardData, isSuperAdminUser, selectedTower]);

  const allTowerCards = useMemo(() => {
    const data = Array.isArray(dashboardData?.data) ? dashboardData.data : [];

    if (isSuperAdminUser && allTowers.length > 0) {
      return buildTowerCardsWithExtraData(allTowers, data);
    }

    return data.map((tower) => ({ ...tower, is_active: true }));
  }, [allTowers, dashboardData, isSuperAdminUser]);

  const catalogTowerCards = useMemo(() => {
    const data = Array.isArray(deviceCatalogData?.data)
      ? deviceCatalogData.data
      : [];
    const dataByTowerId = new Map(data.map((tower) => [tower.tower_id, tower]));

    if (selectedTower) {
      const selectedTowerMeta = allTowers.find(
        (tower) => tower.id === selectedTower,
      );
      const selectedTowerData =
        dataByTowerId.get(selectedTower) ||
        data.find((tower) => tower.tower_id === selectedTower);

      if (selectedTowerData) {
        return [
          {
            ...selectedTowerData,
            tower_id: selectedTower,
            tower_name:
              selectedTowerData.tower_name || selectedTowerMeta?.name || "-",
            tower_location:
              selectedTowerData.tower_location ||
              selectedTowerMeta?.location ||
              "-",
            is_active: selectedTowerMeta?.active ?? true,
            tanks: selectedTowerData.tanks || [],
          },
        ];
      }

      if (selectedTowerMeta) {
        return [
          {
            tower_id: selectedTowerMeta.id,
            tower_name: selectedTowerMeta.name,
            tower_location: selectedTowerMeta.location,
            is_active: Boolean(selectedTowerMeta.active),
            tanks: [],
          },
        ];
      }

      return [];
    }

    if (isSuperAdminUser && allTowers.length > 0) {
      return buildTowerCardsWithExtraData(allTowers, data);
    }

    return data.map((tower) => ({ ...tower, is_active: true }));
  }, [allTowers, deviceCatalogData, isSuperAdminUser, selectedTower]);

  const catalogAllTowerCards = useMemo(() => {
    const data = Array.isArray(deviceCatalogData?.data)
      ? deviceCatalogData.data
      : [];

    if (isSuperAdminUser && allTowers.length > 0) {
      return buildTowerCardsWithExtraData(allTowers, data);
    }

    return data.map((tower) => ({ ...tower, is_active: true }));
  }, [allTowers, deviceCatalogData, isSuperAdminUser]);

  const deviceOptions = useMemo(() => {
    const sourceTowers =
      selectedDeviceType === "level" ? catalogTowerCards : catalogAllTowerCards;
    const options = buildDeviceOptions(sourceTowers, selectedDeviceType);

    if (options.length === 0) return options;

    return [
      {
        key: getAllDevicesSelectionKey(selectedDeviceType),
        type: selectedDeviceType,
        deviceId: ALL_DEVICES_VALUE,
        label: `All ${DEVICE_LABEL_BY_TYPE[selectedDeviceType]}s`,
      },
      ...options,
    ];
  }, [catalogAllTowerCards, catalogTowerCards, selectedDeviceType]);

  const visibleDeviceOptions = useMemo(() => {
    const sourceTowers =
      selectedDeviceType === "level" ? towerCards : allTowerCards;
    return buildDeviceOptions(sourceTowers, selectedDeviceType);
  }, [allTowerCards, selectedDeviceType, towerCards]);

  const showAllDevices = isAllDevicesSelection(selectedDeviceKey);

  useEffect(() => {
    if (
      selectedDeviceKey &&
      deviceOptions.some((option) => option.key === selectedDeviceKey)
    ) {
      return;
    }

    setSelectedDeviceKey(deviceOptions[0]?.key || "");
  }, [deviceOptions, selectedDeviceKey]);

  const selectedDevice = useMemo(() => {
    if (showAllDevices) return null;

    const visibleOption = visibleDeviceOptions.find(
      (option) => option.key === selectedDeviceKey,
    );
    const catalogOption = deviceOptions.find(
      (option) => option.key === selectedDeviceKey,
    );

    return mergeDeviceOptionWithCatalog(visibleOption, catalogOption);
  }, [deviceOptions, selectedDeviceKey, showAllDevices, visibleDeviceOptions]);

  if (loading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="flex items-center gap-3 text-lg text-slate-600">
            <Loader className="h-5 w-5 animate-spin" />
            Loading...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Dashboard</h1>
            <p className="mt-1 text-slate-600">
              Real-time monitoring of water infrastructure
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={selectedDeviceType}
              onValueChange={(value) => {
                setSelectedDeviceType(value);
                setSelectedDeviceKey("");
                if (value !== "level") {
                  setSelectedTower(null);
                }
              }}
            >
              <SelectTrigger className="w-[230px]">
                <SelectValue placeholder="Select device type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="level">ATVC - Valve Controller</SelectItem>
                <SelectItem value="pump">ATPC - Pump Controller</SelectItem>
                <SelectItem value="atbpc">
                  ATBPC - Borewell Pump Controller
                </SelectItem>
              </SelectContent>
            </Select>

            {selectedDeviceType === "level" &&
              isSuperAdmin() &&
              allTowers.length > 0 && (
                <Select
                  value={selectedTower || "all"}
                  onValueChange={(val) => {
                    setSelectedTower(val === "all" ? null : val);
                    setSelectedDeviceKey("");
                  }}
                >
                  <SelectTrigger
                    className="w-[200px]"
                    data-testid="tower-selector"
                  >
                    <SelectValue placeholder="Select Tower" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Towers</SelectItem>
                    {allTowers.map((tower) => (
                      <SelectItem key={tower.id} value={tower.id}>
                        {tower.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

            <Select
              value={selectedDeviceKey || ""}
              onValueChange={setSelectedDeviceKey}
              disabled={deviceOptions.length === 0}
            >
              <SelectTrigger className="w-[270px]">
                <SelectValue
                  placeholder={
                    deviceOptions.length > 0
                      ? "Select device"
                      : "No devices found"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {deviceOptions.map((device) => (
                  <SelectItem key={device.key} value={device.key}>
                    {device.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              className="gap-2"
              disabled={dataRequestKind !== null}
              onClick={() => {
                loadDashboardData(
                  true,
                  getDashboardFiltersForSelection(
                    selectedDeviceKey,
                    selectedTower,
                  ) || {},
                );
              }}
            >
              <RefreshCw
                className={`h-4 w-4 ${dataRequestKind ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </div>

        {dataRequestKind === "filter" ? (
          <div
            className="flex min-h-[40vh] items-center justify-center rounded-3xl border border-slate-200 bg-white shadow-sm"
            data-testid="dashboard-data-loader"
          >
            <div className="flex items-center gap-3 text-lg text-slate-600">
              <Loader className="h-5 w-5 animate-spin" />
              Loading data...
            </div>
          </div>
        ) : selectedDevice ? (
          <div
            className={
              selectedDevice.type === "level" ? LEVEL_CARD_GRID_CLASS : "w-full"
            }
          >
            <ControllerDeviceCard
              selection={selectedDevice}
              mqttConnected={mqttConnected}
              pendingCommands={pendingCommands}
              publishValveToggleCommand={publishValveToggleCommand}
              publishPumpToggleCommand={publishPumpToggleCommand}
              publishRemoteOperationCommand={publishRemoteOperationCommand}
              statusCheckTime={statusCheckTime}
            />
          </div>
        ) : showAllDevices && visibleDeviceOptions.length > 0 ? (
          <div
            className={
              selectedDeviceType === "level"
                ? LEVEL_CARD_GRID_CLASS
                : "grid grid-cols-1 gap-4 lg:grid-cols-2"
            }
          >
            {visibleDeviceOptions.map((device) => {
              const shouldSpanFullRow =
                selectedDeviceType !== "level" &&
                getDeviceCardCount(device) > 1;

              return (
                <div
                  key={device.key}
                  className={
                    shouldSpanFullRow ? "w-full lg:col-span-2" : "w-full"
                  }
                >
                  <ControllerDeviceCard
                    selection={device}
                    mqttConnected={mqttConnected}
                    pendingCommands={pendingCommands}
                    publishValveToggleCommand={publishValveToggleCommand}
                    publishPumpToggleCommand={publishPumpToggleCommand}
                    publishRemoteOperationCommand={
                      publishRemoteOperationCommand
                    }
                    statusCheckTime={statusCheckTime}
                  />
                </div>
              );
            })}
          </div>
        ) : towerCards.length > 0 ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center shadow-sm">
            <Zap className="mx-auto mb-4 h-14 w-14 text-slate-300" />
            <h3 className="mb-2 text-xl font-semibold text-slate-900">
              Select a controller
            </h3>
            <p className="text-slate-600">
              Choose a tower, then select a controller device to view live data.
            </p>
          </div>
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center shadow-sm">
            <Building2 className="mx-auto mb-4 h-16 w-16 text-slate-300" />
            <h3 className="mb-2 text-xl font-semibold text-slate-900">
              No Data Available
            </h3>
            <p className="text-slate-600">
              {isSuperAdmin()
                ? "No towers found. Please register devices in the Device Registration section."
                : "No devices have been mapped to your account yet. Contact your Super Admin."}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

const TowerCard = ({
  tower,
  mqttConnected,
  pendingCommands,
  publishCommand,
  publishValveToggleCommand,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
  statusCheckTime,
}) => {
  const tanks = tower.tanks || [];
  const levelControllers = getControllerList(
    tower,
    "level_controllers",
    "level_controller",
  );
  const pumpControllers = getControllerList(
    tower,
    "pump_controllers",
    "pump_controller",
  );
  const activeLevelCount = levelControllers.filter((controller) =>
    isControllerActive(controller?.timestamp, statusCheckTime),
  ).length;
  const activePumpCount = pumpControllers.filter((controller) =>
    isControllerActive(controller?.timestamp, statusCheckTime),
  ).length;
  const isLevelControllerActive = activeLevelCount > 0;
  const isPumpControllerActive = activePumpCount > 0;
  const hasActiveController = isLevelControllerActive || isPumpControllerActive;
  const battery =
    levelControllers.find((controller) => controller?.battery_level != null)
      ?.battery_level ??
    pumpControllers.find((controller) => controller?.battery_level != null)
      ?.battery_level ??
    tanks[0]?.level_controller_battery;
  const hasFault = tanks.some(
    (tank) =>
      tank.pump_packet?.pump_health &&
      tank.pump_packet.pump_health !== "no_fault",
  );

  return (
    <div
      className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-md shadow-slate-200/60"
      data-testid={`tower-card-${tower.tower_id}`}
    >
      <div className="flex items-start justify-between border-b border-slate-100 px-4 py-3 sm:px-5">
        <div className="flex items-start gap-3">
          <span
            className={`mt-1.5 h-3 w-3 rounded-full ${hasActiveController ? "bg-green-500 shadow-[0_0_0_5px_rgba(34,197,94,0.12)]" : "bg-slate-300"}`}
          />
          <div>
            <h2 className="text-lg font-bold text-slate-950">
              {tower.tower_name}
            </h2>
            <p className="mt-0.5 text-sm font-medium text-slate-400">
              {tower.tower_location || "-"}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2">
          <ControllerStatusBadge
            label={`Level ${activeLevelCount}/${levelControllers.length || 0}`}
            active={isLevelControllerActive}
          />
          <ControllerStatusBadge
            label={`Pump ${activePumpCount}/${pumpControllers.length || 0}`}
            active={isPumpControllerActive}
          />
        </div>
      </div>

      <div className="space-y-3 px-4 py-4 sm:px-5">
        {tanks.length > 0 ? (
          tanks.map((tank, index) => (
            <div
              key={`${tank.tank_id}-${tank.valve_id}`}
              className={index > 0 ? "border-t border-slate-200 pt-5 mt-5" : ""}
            >
              <TankPanel
                tank={tank}
                tower={tower}
                mqttConnected={mqttConnected}
                pendingCommands={pendingCommands}
                publishCommand={publishCommand}
                publishValveToggleCommand={publishValveToggleCommand}
                publishPumpToggleCommand={publishPumpToggleCommand}
                publishRemoteOperationCommand={publishRemoteOperationCommand}
              />
            </div>
          ))
        ) : (
          <div className="py-7 text-center text-lg text-slate-400">
            No tanks configured
          </div>
        )}
      </div>

      {tanks.length > 0 && (
        <div className="flex items-center gap-5 border-t border-slate-100 px-4 py-3 text-xs sm:px-5">
          <span className="flex items-center gap-2 font-semibold text-slate-600">
            <Battery className="h-3.5 w-3.5" />
            {formatWholePercent(battery)}%
          </span>
          <span
            className={`flex items-center gap-2 font-semibold ${hasFault ? "text-red-500" : "text-emerald-500"}`}
          >
            <Heart className="h-3.5 w-3.5" />
            {hasFault ? "Fault" : "Healthy"}
          </span>
        </div>
      )}
    </div>
  );
};

const ControllerStatusBadge = ({ label, active }) => (
  <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
    <span
      className={`h-2 w-2 rounded-full ${active ? "bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]" : "bg-slate-300"}`}
    />
    <span className="min-w-[72px] text-slate-500">{label}</span>
    <span className={active ? "text-emerald-600" : "text-slate-400"}>
      {active ? "Active" : "Inactive"}
    </span>
  </div>
);

const ControllerDeviceCard = ({
  selection,
  mqttConnected,
  pendingCommands,
  publishValveToggleCommand,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
  statusCheckTime,
}) => {
  if (selection.type === "level") {
    return (
      <LevelControllerCard
        tower={selection.tower}
        controller={selection.controller}
        mqttConnected={mqttConnected}
        pendingCommands={pendingCommands}
        publishValveToggleCommand={publishValveToggleCommand}
        publishRemoteOperationCommand={publishRemoteOperationCommand}
        statusCheckTime={statusCheckTime}
      />
    );
  }

  if (selection.type === "pump") {
    return (
      <PumpControllerCard
        tower={selection.tower}
        controller={selection.controller}
        mqttConnected={mqttConnected}
        pendingCommands={pendingCommands}
        publishPumpToggleCommand={publishPumpToggleCommand}
        publishRemoteOperationCommand={publishRemoteOperationCommand}
        statusCheckTime={statusCheckTime}
      />
    );
  }

  return (
    <ATBPCControllerCard
      tower={selection.tower}
      controller={selection.controller}
      mqttConnected={mqttConnected}
      pendingCommands={pendingCommands}
      publishPumpToggleCommand={publishPumpToggleCommand}
      publishRemoteOperationCommand={publishRemoteOperationCommand}
      statusCheckTime={statusCheckTime}
    />
  );
};

// A tank can reach a card twice (reported by the device and listed from the
// flow configuration). Show it once, keeping the copy that has live data and a
// valve, and filling any gaps from the other copy.
const tankRank = (tank) =>
  (tank.has_live_data === false ? 0 : 2) + (isUsableReference(tank.valve_id) ? 1 : 0);

const dedupeControllerTanks = (tanks) => {
  const byTankId = new Map();
  tanks.forEach((tank) => {
    const key = String(tank.tank_id ?? "").trim().toLowerCase();
    const existing = byTankId.get(key);
    if (!existing) {
      byTankId.set(key, tank);
      return;
    }
    const [preferred, other] =
      tankRank(tank) > tankRank(existing) ? [tank, existing] : [existing, tank];
    const merged = { ...preferred };
    Object.entries(other).forEach(([field, value]) => {
      if (merged[field] === null || merged[field] === undefined) {
        merged[field] = value;
      }
    });
    byTankId.set(key, merged);
  });
  return Array.from(byTankId.values());
};

const LevelControllerCard = ({
  tower,
  controller,
  mqttConnected,
  pendingCommands,
  publishValveToggleCommand,
  publishRemoteOperationCommand,
  statusCheckTime,
}) => {
  const deviceId = controller.device_id;
  const active = isControllerActive(controller?.timestamp, statusCheckTime);
  const tanks = dedupeControllerTanks(
    (tower.tanks || []).filter(
      (tank) =>
        getUsableReference(
          tank.level_controller_device_id,
          tower.level_controller?.device_id,
        ) === deviceId,
    ),
  );
  const battery =
    controller.battery_level ?? tanks[0]?.level_controller_battery ?? null;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-md shadow-slate-200/60">
      <ControllerCardHeader
        tower={tower}
        title={controller.name || deviceId}
        subtitle={deviceId}
        activeLabel={
          <>
            <span style={{ color: active ? "green" : "red" }}>
              {active ? "Active" : "Inactive"}
            </span>
          </>
        }
        active={active}
        packetTimestamp={controller.timestamp}
        showTowerDetails={true}
      />

      <div className="flex-1 space-y-5 px-4 py-4 sm:px-5">
        {tanks.length > 0 ? (
          tanks.map((tank, index) => (
            <div
              key={`${deviceId}-${tank.tank_id}-${tank.valve_id || index}`}
              className={index > 0 ? "border-t border-slate-200 pt-5" : ""}
            >
              <LevelTankPanel
                tank={tank}
                tower={tower}
                controller={controller}
                mqttConnected={mqttConnected}
                pendingCommands={pendingCommands}
                publishValveToggleCommand={publishValveToggleCommand}
                publishRemoteOperationCommand={publishRemoteOperationCommand}
              />
            </div>
          ))
        ) : (
          <EmptyCardMessage message="No tanks are mapped to this valve controller" />
        )}
      </div>

      <ControllerCardFooter
        battery={battery}
        healthText="Healthy"
        healthy={true}
      />
    </div>
  );
};

const LevelTankPanel = ({
  tank,
  tower,
  controller,
  mqttConnected,
  pendingCommands,
  publishValveToggleCommand,
  publishRemoteOperationCommand,
}) => {
  const deviceId = controller.device_id;
  const valveId = getUsableReference(tank.valve_id);
  const valveDisplayId = getUsableReference(
    tank.valve_custom_id,
    tank.valve_id,
  );
  const percentage = formatWholePercent(tank.tank_percentage);
  const tankHigh = Number(tank.high);
  const isTankAboveHigh =
    typeof percentage === "number" &&
    Number.isFinite(tankHigh) &&
    percentage > tankHigh;
  const valveState = getUsableReference(tank.valve_state);
  const valveOn = Boolean(valveState && isOn(valveState) && !isTankAboveHigh);
  const operationMode = normalize(
    tank.level_controller_mode || controller.operation_mode,
  );
  const remoteMode = normalize(
    tank.level_controller_remote || controller.remote_mode,
  );
  const canControlValve = Boolean(
    mqttConnected &&
    deviceId &&
    valveId &&
    remoteMode === "on" &&
    (valveOn || !isTankAboveHigh),
  );
  const canSendRemote = Boolean(mqttConnected && deviceId && valveId);
  const pendingCommandList = Object.values(pendingCommands);
  const pending = pendingCommandList.some(
    (command) =>
      (command.type === "valve" &&
        command.deviceId === deviceId &&
        command.targetId === valveId) ||
      (command.type === "remote" &&
        command.controllerType === "level" &&
        command.deviceId === deviceId),
  );

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-[82px_1fr] md:items-center">
        <div className="flex justify-center md:block">
          <TankGauge
            percentage={percentage}
            timestamp={tank.level_controller_timestamp || controller.timestamp}
            showTimestamp={false}
          />
        </div>
        <div className="min-w-0">
          <h3 className="truncate text-lg font-bold text-slate-950">
            {tank.tank_name || tank.tank_custom_id || tank.tank_id}
          </h3>
          <StatusLine
            active={valveOn}
            label={
              valveState ? `Valve ${valveOn ? "Open" : "Close"}` : "Valve N/A"
            }
          />
          {tank.has_live_data === false && (
            <p className="mt-1 text-xs font-medium text-amber-600">
              No live data: device is not reporting this tank
            </p>
          )}
        </div>
      </div>

      <ControlRow
        type="valve"
        title={formatControlTitle("Valve", valveDisplayId)}
        icon={<SlidersHorizontal className="h-5 w-5" />}
        mode={operationMode}
        remoteMode={remoteMode}
        checked={valveOn}
        disabled={!canControlValve}
        remoteDisabled={!canSendRemote}
        pending={pending}
        onRemoteToggle={(enabled) =>
          publishRemoteOperationCommand({
            societyId: tower.society_id,
            deviceId,
            controllerType: "level",
            remoteOperations: enabled ? "on" : "off",
          })
        }
        onToggle={(checked) =>
          publishValveToggleCommand({
            societyId: tower.society_id,
            deviceId,
            valveId,
            operation: checked ? "open" : "close",
          })
        }
      />
    </div>
  );
};

const PumpControllerCard = ({
  tower,
  controller,
  mqttConnected,
  pendingCommands,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
  statusCheckTime,
}) => {
  const deviceId = controller.device_id;
  const active = isControllerActive(controller?.timestamp, statusCheckTime);
  const pumpStatuses = getPumpControllerStatuses(tower, controller);

  if (pumpStatuses.length === 0) {
    return (
      <PumpInstanceCard
        tower={tower}
        controller={controller}
        pumpStatus={null}
        active={active}
        mqttConnected={mqttConnected}
        pendingCommands={pendingCommands}
        publishPumpToggleCommand={publishPumpToggleCommand}
        publishRemoteOperationCommand={publishRemoteOperationCommand}
      />
    );
  }

  if (pumpStatuses.length === 1) {
    return (
      <PumpInstanceCard
        tower={tower}
        controller={controller}
        pumpStatus={pumpStatuses[0]}
        active={active}
        mqttConnected={mqttConnected}
        pendingCommands={pendingCommands}
        publishPumpToggleCommand={publishPumpToggleCommand}
        publishRemoteOperationCommand={publishRemoteOperationCommand}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {pumpStatuses.map((status, index) => (
        <PumpInstanceCard
          key={status.pump_id || `${deviceId}-${index}`}
          tower={tower}
          controller={controller}
          pumpStatus={status}
          active={active}
          mqttConnected={mqttConnected}
          pendingCommands={pendingCommands}
          publishPumpToggleCommand={publishPumpToggleCommand}
          publishRemoteOperationCommand={publishRemoteOperationCommand}
        />
      ))}
    </div>
  );
};

const PumpInstanceCard = ({
  tower,
  controller,
  pumpStatus,
  active,
  mqttConnected,
  pendingCommands,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
}) => {
  const deviceId = controller.device_id;
  const pumpId = pumpStatus?.pump_id || "-";
  const hasFault =
    pumpStatus?.pump_health && pumpStatus.pump_health !== "no_fault";

  return (
    <div className="flex min-h-[520px] flex-col overflow-hidden rounded-xl border border-blue-100 bg-white shadow-md shadow-blue-100/70 ring-1 ring-blue-50">
      <ControllerCardHeader
        tower={tower}
        title={controller.name || deviceId}
        subtitle={deviceId}
        activeLabel={
          <>
            <span style={{ color: active ? "green" : "red" }}>
              {active ? "Active" : "Inactive"}
            </span>
          </>
        }
        active={active}
        packetTimestamp={controller.timestamp || pumpStatus?.atbc_timestamp}
      />

      <div className="flex-1 space-y-3 px-3 py-3 sm:px-4">
        {pumpStatus?.has_live_data === false && <NoLiveDataNotice />}
        <ConnectedUgtLevel
          ugtId={pumpStatus?.ugt_id}
          ugtLevel={pumpStatus?.ugt_level}
        />

        <PumpControlPanel
          tower={tower}
          controller={controller}
          pumpStatus={pumpStatus || { pump_id: pumpId }}
          pumpName={controller.pump_names?.[pumpId]}
          mqttConnected={mqttConnected}
          pendingCommands={pendingCommands}
          publishPumpToggleCommand={publishPumpToggleCommand}
          publishRemoteOperationCommand={publishRemoteOperationCommand}
        />

        <PumpDemandGrid
          tower={tower}
          pumpStatuses={pumpStatus ? [pumpStatus] : []}
          connectedTanks={controller.connected_tanks || {}}
        />
        <PumpControlReadings
          pumpStatus={pumpStatus}
          pumpName={controller.pump_names?.[pumpId]}
        />
        <EnergyMeterGrid
          pumpStatuses={[pumpStatus || { pump_id: pumpId }]}
          showPumpId={false}
        />
      </div>

      <ControllerCardFooter
        battery={controller.battery_level}
        healthText={
          hasFault ? getHealthText(pumpStatus?.pump_health) : "Healthy"
        }
        healthy={!hasFault}
      />
    </div>
  );
};

// The pump's underground tank level, shown as its own section ("ugkh001 – 100%").
const ConnectedUgtLevel = ({ ugtId, ugtLevel }) => {
  const name = isUsableReference(ugtId) ? ugtId : "-";
  const level = formatUgtLevel(ugtLevel);

  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
      <h4 className="mb-2 text-sm font-bold text-slate-800">
        Connected UGT Level
      </h4>
      <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <Waves className="h-4 w-4" />
        </span>
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
          {name} – {level}
        </p>
      </div>
    </div>
  );
};

// Configured pumps are always shown; live packet statuses fill them in, and
// any extra pump the device reports is appended.
const mergeConfiguredPumpStatuses = (statuses, configuredPumpIds) => {
  const statusByPumpId = new Map();
  statuses.forEach((status) => {
    const pumpId = getUsableReference(status?.pump_id);
    if (pumpId && !statusByPumpId.has(pumpId)) {
      statusByPumpId.set(pumpId, status);
    }
  });

  const configured = configuredPumpIds
    .map((pumpId) => getUsableReference(pumpId))
    .filter(Boolean);
  const configuredSet = new Set(configured);

  return [
    ...configured.map(
      (pumpId) =>
        statusByPumpId.get(pumpId) || { pump_id: pumpId, has_live_data: false },
    ),
    ...statuses.filter(
      (status) => !configuredSet.has(getUsableReference(status?.pump_id)),
    ),
  ];
};

const getPumpControllerStatuses = (tower, controller) => {
  const controllerStatuses = Array.isArray(controller.status)
    ? controller.status
    : [];
  const configuredPumpIds = Array.isArray(controller.configured_pump_ids)
    ? controller.configured_pump_ids
    : [];
  if (controllerStatuses.length > 0 || configuredPumpIds.length > 0) {
    return mergeConfiguredPumpStatuses(controllerStatuses, configuredPumpIds);
  }

  const statuses = [];
  const seenPumpIds = new Set();
  (tower.tanks || []).forEach((tank) => {
    const pumpPacket = tank.pump_packet;
    const pumpId = getUsableReference(
      pumpPacket?.pump_id,
      tank.pump?.pump_custom_id,
      tank.pump?.pump_id,
    );
    const tankPumpControllerId = getUsableReference(
      tank.pump_controller_device_id,
      tower.pump_controller?.device_id,
    );

    if (
      pumpPacket &&
      pumpId &&
      tankPumpControllerId === controller.device_id &&
      !seenPumpIds.has(pumpId)
    ) {
      seenPumpIds.add(pumpId);
      statuses.push({ ...pumpPacket, pump_id: pumpId });
    }
  });
  return statuses;
};

const PumpControlPanel = ({
  tower,
  controller,
  pumpStatus,
  pumpName,
  mqttConnected,
  pendingCommands,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
}) => {
  const deviceId = controller.device_id;
  const pumpId = getUsableReference(pumpStatus.pump_id);
  const pumpOn = isOn(pumpStatus.pump_state);
  const operationMode = normalize(
    pumpStatus.atbc_mode || controller.operation_mode,
  );
  const remoteMode = normalize(
    pumpStatus.atbc_remote || controller.remote_mode,
  );
  const canControlPump = Boolean(
    mqttConnected && deviceId && pumpId && isOn(remoteMode),
  );
  const canSendRemote = Boolean(mqttConnected && deviceId && pumpId);
  const pending = Object.values(pendingCommands).some(
    (command) =>
      (command.type === "pump" &&
        command.deviceId === deviceId &&
        command.targetId === pumpId) ||
      (command.type === "remote" &&
        command.controllerType === "pump" &&
        command.deviceId === deviceId),
  );

  return (
    <ControlRow
      type="pump"
      title={pumpName || formatControlTitle("Pump", pumpId)}
      icon={<Power className="h-5 w-5" />}
      mode={operationMode}
      remoteMode={remoteMode}
      showUgtLevel={false}
      checked={pumpOn}
      disabled={!canControlPump}
      remoteDisabled={!canSendRemote}
      pending={pending}
      onRemoteToggle={(enabled) =>
        publishRemoteOperationCommand({
          societyId: tower.society_id,
          deviceId,
          controllerType: "pump",
          remoteOperations: enabled ? "on" : "off",
        })
      }
      onToggle={(checked) =>
        publishPumpToggleCommand({
          societyId: tower.society_id,
          deviceId,
          pumpId,
          operation: checked ? "on" : "off",
        })
      }
    />
  );
};

const ATBPCControllerCard = ({
  tower,
  controller,
  mqttConnected,
  pendingCommands,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
  statusCheckTime,
}) => {
  const deviceId = controller.device_id;
  const active = isControllerActive(controller?.timestamp, statusCheckTime);
  const pumpStatuses = getATBPCControllerStatuses(controller);

  if (pumpStatuses.length === 0) {
    return (
      <ATBPCPumpCard
        tower={tower}
        controller={controller}
        pumpStatus={null}
        active={active}
        mqttConnected={mqttConnected}
        pendingCommands={pendingCommands}
        publishPumpToggleCommand={publishPumpToggleCommand}
        publishRemoteOperationCommand={publishRemoteOperationCommand}
      />
    );
  }

  if (pumpStatuses.length === 1) {
    return (
      <ATBPCPumpCard
        tower={tower}
        controller={controller}
        pumpStatus={pumpStatuses[0]}
        active={active}
        mqttConnected={mqttConnected}
        pendingCommands={pendingCommands}
        publishPumpToggleCommand={publishPumpToggleCommand}
        publishRemoteOperationCommand={publishRemoteOperationCommand}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {pumpStatuses.map((status, index) => (
        <ATBPCPumpCard
          key={status.pump_id || `${deviceId}-${index}`}
          tower={tower}
          controller={controller}
          pumpStatus={status}
          active={active}
          mqttConnected={mqttConnected}
          pendingCommands={pendingCommands}
          publishPumpToggleCommand={publishPumpToggleCommand}
          publishRemoteOperationCommand={publishRemoteOperationCommand}
        />
      ))}
    </div>
  );
};

const getATBPCControllerStatuses = (controller) => {
  const controllerStatuses = Array.isArray(controller.status)
    ? controller.status
    : [];
  const mappings = Array.isArray(controller.pump_configurations)
    ? controller.pump_configurations
    : [];

  // Every configured pump is shown, with live packet data when the device
  // reports it; a pump missing from the packet must not hide the others.
  return mappings
    .filter(
      (mapping) =>
        isUsableReference(mapping.pump_id) &&
        (isUsableReference(mapping.source_id) ||
          isUsableReference(mapping.destination_tank_id)),
    )
    .filter(
      (mapping, index, list) =>
        list.findIndex(
          (item) =>
            getUsableReference(item.pump_id) ===
            getUsableReference(mapping.pump_id),
        ) === index,
    )
    .map((mapping) => {
      const status = controllerStatuses.find(
        (item) =>
          getUsableReference(item.pump_id) ===
          getUsableReference(mapping.pump_id),
      );

      return {
        ...(status || { pump_id: mapping.pump_id, has_live_data: false }),
        source:
          status?.source ||
          (mapping.source_id ? { id: mapping.source_id } : undefined),
        destination:
          status?.destination ||
          (mapping.destination_tank_id
            ? { tank1: { id: mapping.destination_tank_id } }
            : undefined),
        flowmeter_id: status?.flowmeter_id || mapping.flowmeter_id,
      };
    });
};

const ATBPCPumpCard = ({
  tower,
  controller,
  pumpStatus,
  active,
  mqttConnected,
  pendingCommands,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
}) => {
  const deviceId = controller.device_id;
  const pumpId = pumpStatus?.pump_id || "-";
  const assetNames = controller.asset_names || {};
  const hasFault =
    pumpStatus?.pump_health && pumpStatus.pump_health !== "no_fault";

  return (
    <div className="flex min-h-[520px] flex-col overflow-hidden rounded-xl border border-blue-100 bg-white shadow-md shadow-blue-100/70 ring-1 ring-blue-50">
      <ControllerCardHeader
        tower={tower}
        title={controller.name || deviceId}
        subtitle={deviceId}
        activeLabel={
          <>
            <span style={{ color: active ? "green" : "red" }}>
              {active ? "Active" : "Inactive"}
            </span>
          </>
        }
        active={active}
        packetTimestamp={controller.timestamp || pumpStatus?.atbpc_timestamp}
      />

      <div className="flex-1 space-y-3 px-3 py-3 sm:px-4">
        {pumpStatus?.has_live_data === false && <NoLiveDataNotice />}
        <ATBPCSourceDestinationGrid
          pumpStatus={pumpStatus || {}}
          assetNames={assetNames}
        />
        <ATBPCPumpControlPanel
          tower={tower}
          controller={controller}
          pumpStatus={pumpStatus || { pump_id: pumpId }}
          pumpName={assetNames[pumpId]}
          mqttConnected={mqttConnected}
          pendingCommands={pendingCommands}
          publishPumpToggleCommand={publishPumpToggleCommand}
          publishRemoteOperationCommand={publishRemoteOperationCommand}
        />
        <EnergyMeterGrid
          pumpStatuses={[pumpStatus || { pump_id: pumpId }]}
          showPumpId={false}
        />
        <FlowmeterGrid pumpStatus={pumpStatus || { pump_id: pumpId }} />
      </div>

      <ControllerCardFooter
        battery={controller.battery_level}
        healthText={
          hasFault ? getHealthText(pumpStatus?.pump_health) : "Healthy"
        }
        healthy={!hasFault}
      />
    </div>
  );
};

const ATBPCSourceDestinationGrid = ({ pumpStatus, assetNames = {} }) => {
  const source = pumpStatus.source || {};
  const sourceId = getUsableReference(source.id, source.source_id);
  const sourceType = source.type;
  const sourceLevel = source.level;
  const destinations = getATBPCDestinationRows(pumpStatus.destination);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-cyan-600 shadow-sm">
            <Droplet className="h-4 w-4" />
          </span>
          <h4 className="text-sm font-bold text-slate-800">Source</h4>
        </div>
        <div className="rounded-lg border border-cyan-100 bg-white px-3 py-2">
          <div className="flex items-center justify-between gap-3 text-sm font-semibold">
            <span className="text-slate-800">
              {(sourceId && assetNames[sourceId]) || sourceId || "-"}
            </span>
            <span className="text-cyan-700">
              {formatATBPCSourceLevel(sourceLevel, sourceType)}
            </span>
          </div>
          <p className="mt-1 text-xs font-semibold capitalize text-slate-400">
            {sourceId ? formatSourceType(sourceType) : "-"}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm">
            <Waves className="h-4 w-4" />
          </span>
          <h4 className="text-sm font-bold text-slate-800">Destination</h4>
        </div>
        <div className="space-y-2">
          {(destinations.length
            ? destinations
            : [{ id: "-", level_pct: "-" }]
          ).map((destination, index) => (
            <div
              key={`${destination.id}-${index}`}
              className="rounded-lg border border-blue-100 bg-white px-3 py-2"
            >
              <div className="flex items-center justify-between gap-3 text-sm font-semibold">
                <span className="text-slate-800">
                  {assetNames[destination.id] || destination.id}
                </span>
                <span className="text-blue-700">
                  {formatOptionalPercent(destination.level_pct)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const getATBPCDestinationRows = (destination) => {
  if (!destination || typeof destination !== "object") return [];
  if (
    isUsableReference(destination.id) ||
    isUsableReference(destination.tank_id)
  ) {
    return [
      {
        id: getUsableReference(destination.id, destination.tank_id),
        level_pct: destination.level_pct,
      },
    ];
  }

  return Object.values(destination)
    .filter((item) => item && typeof item === "object")
    .map((item) => ({
      id: getUsableReference(item.id, item.tank_id),
      level_pct: item.level_pct,
    }))
    .filter((item) => item.id);
};

const formatSourceType = (type) => {
  if (type === 0 || type === "0") return "Borewell";
  if (type === 1 || type === "1") return "Tank";
  return isUsableReference(type) ? String(type) : "Source";
};

const ATBPCPumpControlPanel = ({
  tower,
  controller,
  pumpStatus,
  pumpName,
  mqttConnected,
  pendingCommands,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
}) => {
  const deviceId = controller.device_id;
  const pumpId = getUsableReference(pumpStatus.pump_id);
  const pumpOn = isOn(pumpStatus.pump_state);
  const operationMode = normalize(
    pumpStatus.atbpc_mode || controller.operation_mode,
  );
  const remoteMode = normalize(
    pumpStatus.atbpc_remote || controller.remote_mode,
  );
  const canControlPump = Boolean(
    mqttConnected && deviceId && pumpId && isOn(remoteMode),
  );
  const canSendRemote = Boolean(mqttConnected && deviceId && pumpId);
  const pending = Object.values(pendingCommands).some(
    (command) =>
      (command.type === "pump" &&
        command.deviceId === deviceId &&
        command.targetId === pumpId) ||
      (command.type === "remote" &&
        command.controllerType === "atbpc" &&
        command.deviceId === deviceId),
  );

  return (
    <ControlRow
      type="pump"
      title={pumpName || formatControlTitle("Pump", pumpId)}
      icon={<Power className="h-5 w-5" />}
      mode={operationMode}
      remoteMode={remoteMode}
      checked={pumpOn}
      disabled={!canControlPump}
      remoteDisabled={!canSendRemote}
      showUgtLevel={false}
      pending={pending}
      onRemoteToggle={(enabled) =>
        publishRemoteOperationCommand({
          societyId: tower.society_id,
          deviceId,
          controllerType: "atbpc",
          remoteOperations: enabled ? "on" : "off",
        })
      }
      onToggle={(checked) =>
        publishPumpToggleCommand({
          societyId: tower.society_id,
          deviceId,
          pumpId,
          operation: checked ? "on" : "off",
        })
      }
    />
  );
};

const PumpDemandGrid = ({ tower, pumpStatuses, connectedTanks = {} }) => {
  const demandRows = pumpStatuses.flatMap((status) =>
    (Array.isArray(status.demand) ? status.demand : []).map((item) => ({
      ...item,
      pump_id: status.pump_id,
    })),
  );
  const tankRows = demandRows.length
    ? demandRows
    : (tower.tanks || []).map((tank) => ({
        tank_id: tank.tank_id,
        demand: 0,
      }));

  if (tankRows.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
      <h4 className="mb-2 text-sm font-bold text-slate-800">Connected Tanks</h4>
      <div className="grid gap-2 sm:grid-cols-2">
        {tankRows.map((row, index) => {
          // This tower's tank (live-updated) first; a pump can also supply
          // tanks in other towers, which come from the pump's connected_tanks.
          const tank = (tower.tanks || []).find(
            (item) => item.tank_id === row.tank_id,
          );
          const otherTowerTank = connectedTanks[row.tank_id];
          const percentage = formatWholePercent(
            tank ? tank.tank_percentage : otherTowerTank?.tank_percentage,
          );
          const tankName = tank?.tank_name || otherTowerTank?.name || row.tank_id;
          const needsWater = Number(row.demand) === 1;
          return (
            <div
              key={`${row.pump_id || "tank"}-${row.tank_id}-${index}`}
              className={`rounded-lg border px-3 py-2 ${
                needsWater
                  ? "border-red-200 bg-red-50 text-red-800"
                  : "border-slate-200 bg-white text-slate-700"
              }`}
            >
              <div className="flex items-center justify-between gap-3 text-sm font-semibold">
                <span>{tankName}</span>
                <span>
                  {typeof percentage === "number" ? `${percentage}%` : "-"}
                </span>
              </div>
              {needsWater && (
                <p className="mt-1 text-xs font-semibold">Demanding water</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DIRECT_PUMP_CONTROL_TYPE = 0;
const VFD_PUMP_CONTROL_TYPES = [1, 3]; // VFD, VFD_Direct
const PRESSURE_RANGE_PUMP_CONTROL_TYPE = 2;

const hasReadingBlock = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

// Which readings a pump card shows follows the pump's control type:
// VFD / VFD_Direct -> VFD pressure + frequency only; PressureRange -> pressure
// range only; Direct -> neither. Packets carry both blocks regardless, so the
// control type decides. Only when it is missing does the card fall back to
// whichever block the packet has.
const getPumpControlReadings = (pumpStatus) => {
  const rawControlType = pumpStatus?.control_type;
  const controlType =
    rawControlType === null || rawControlType === undefined || rawControlType === ""
      ? null
      : Number(rawControlType);
  const vfd = hasReadingBlock(pumpStatus?.vfd) ? pumpStatus.vfd : null;
  const pressureRange = hasReadingBlock(pumpStatus?.pressure_range)
    ? pumpStatus.pressure_range
    : null;

  if (VFD_PUMP_CONTROL_TYPES.includes(controlType)) {
    return { vfd, pressureRange: null };
  }
  if (controlType === PRESSURE_RANGE_PUMP_CONTROL_TYPE) {
    return { vfd: null, pressureRange };
  }
  if (controlType === DIRECT_PUMP_CONTROL_TYPE) {
    return { vfd: null, pressureRange: null };
  }
  return vfd ? { vfd, pressureRange: null } : { vfd: null, pressureRange };
};

const ReadingsGrid = ({ title, fields, readings }) => (
  <div className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
    <h4 className="mb-2 text-sm font-bold text-slate-800">{title}</h4>
    <div className="grid gap-2 sm:grid-cols-2 2xl:grid-cols-4">
      {fields.map(([label, getValue]) => (
        <div key={label} className="rounded-md bg-slate-50 px-2.5 py-2">
          <p className="text-[11px] font-semibold uppercase text-slate-400">
            {label}
          </p>
          <p className="text-sm font-bold text-slate-800">
            {getValue(readings) ?? "-"}
          </p>
        </div>
      ))}
    </div>
  </div>
);

const VFD_PRESSURE_READING_FIELDS = [
  ["Out (bar)", (readings) => readings.out_pressure],
  ["Low (bar)", (readings) => readings.pressure_low],
  ["High (bar)", (readings) => readings.pressure_high],
];

const PRESSURE_RANGE_READING_FIELDS = [
  ["Out (bar)", (readings) => readings.out_pressure],
  ["Low (bar)", (readings) => readings.pressure_low],
  ["High (bar)", (readings) => readings.pressure_high],
];

const FREQUENCY_READING_FIELDS = [
  ["Out (Hz)", (readings) => readings.out_freq],
  ["In (Hz)", (readings) => readings.in_freq ?? readings.set_freq],
  ["Min (Hz)", (readings) => readings.min_freq],
  ["Max (Hz)", (readings) => readings.max_freq],
];

const PumpControlReadings = ({ pumpStatus, pumpName }) => {
  const { vfd, pressureRange } = getPumpControlReadings(pumpStatus);
  if (!vfd && !pressureRange) return null;

  const pumpLabel = pumpName || pumpStatus?.pump_id || "-";

  return (
    <div className="space-y-3">
      {vfd && (
        <>
          <ReadingsGrid
            title={`VFD Pressure - ${pumpLabel}`}
            fields={VFD_PRESSURE_READING_FIELDS}
            readings={vfd}
          />
          <ReadingsGrid
            title={`VFD Frequency - ${pumpLabel}`}
            fields={FREQUENCY_READING_FIELDS}
            readings={vfd}
          />
        </>
      )}
      {pressureRange && (
        <ReadingsGrid
          title={`Pressure Range - ${pumpLabel}`}
          fields={PRESSURE_RANGE_READING_FIELDS}
          readings={pressureRange}
        />
      )}
    </div>
  );
};

// An energy meter that isn't connected sends "-" (or nothing) for every
// reading. Such a meter has no section on the card.
const hasEnergyMeterReading = (status) =>
  Object.values(status?.energy_meter || {}).some(
    (value) =>
      value !== null &&
      value !== "" &&
      typeof value !== "boolean" &&
      Number.isFinite(Number(value)),
  );

const EnergyMeterGrid = ({ pumpStatuses, showPumpId = true }) => {
  const energyStatuses = pumpStatuses.filter(hasEnergyMeterReading);
  if (energyStatuses.length === 0) return null;

  const fields = [
    ["voltage_r", "Voltage R"],
    ["voltage_y", "Voltage Y"],
    ["voltage_b", "Voltage B"],
    ["avg_vll", "Avg VLL"],
    ["current_r", "Current R"],
    ["current_y", "Current Y"],
    ["current_b", "Current B"],
    ["pf_r", "PF R"],
    ["pf_y", "PF Y"],
    ["pf_b", "PF B"],
    ["avg_pf", "Avg PF"],
    ["frequency", "Frequency"],
    ["total_kw", "Total kW"],
    ["total_kwh", "Total kWh"],
  ];

  return (
    <div className="space-y-3">
      {energyStatuses.map((status) => (
        <div
          key={`em-${status.pump_id}`}
          className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm"
        >
          <h4 className="mb-2 text-sm font-bold text-slate-800">
            {showPumpId ? `Energy Meter - ${status.pump_id}` : "Energy Meter"}
          </h4>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {fields.map(([key, label]) => (
              <div key={key} className="rounded-md bg-slate-50 px-2.5 py-2">
                <p className="text-[11px] font-semibold uppercase text-slate-400">
                  {label}
                </p>
                <p className="text-sm font-bold text-slate-800">
                  {status.energy_meter?.[key] ?? "-"}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const FlowmeterGrid = ({ pumpStatus }) => {
  const fields = [
    ["flow_rate_m3h", "Flow Rate m3/h"],
    ["totalizer_m3", "Totalizer m3"],
    ["used_today_m3", "Used Today m3"],
  ];

  return (
    <div className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
      <div className="mb-2 flex items-center gap-2">
        <Activity className="h-4 w-4 text-cyan-600" />
        <h4 className="text-sm font-bold text-slate-800">Flowmeter</h4>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {fields.map(([key, label]) => (
          <div key={key} className="rounded-md bg-slate-50 px-2.5 py-2">
            <p className="text-[11px] font-semibold uppercase text-slate-400">
              {label}
            </p>
            <p className="text-sm font-bold text-slate-800">
              {pumpStatus?.flowmeter?.[key] ?? "-"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const ControllerCardHeader = ({
  tower,
  title,
  subtitle,
  activeLabel,
  active,
  packetTimestamp,
  showTowerDetails = false,
}) => {
  const hasMappedTower =
    showTowerDetails &&
    isUsableReference(tower?.tower_id) &&
    tower.tower_id !== NO_TOWER_DASHBOARD_ID &&
    isUsableReference(tower?.tower_name);
  const headerTitle = showTowerDetails
    ? hasMappedTower
      ? tower.tower_name
      : "No Tower"
    : title;
  const headerSubtitle = showTowerDetails ? title : subtitle;
  const detailLine = showTowerDetails ? subtitle : null;

  return (
    <div className="flex items-start justify-between border-b border-slate-100 px-3 py-2.5 sm:px-4">
      <div className="flex items-start gap-3">
        <div>
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-base font-bold text-slate-950">
              {headerTitle}
            </h2>
            {showTowerDetails && hasMappedTower && (
              <span className="text-sm font-semibold text-slate-400">
                {tower.tower_location || "-"}
              </span>
            )}
          </div>
          {headerSubtitle && (
            <p className="mt-1.5 text-sm font-semibold text-slate-700">
              {headerSubtitle}
            </p>
          )}
          {detailLine && (
            <p className="text-xs font-semibold text-slate-400">{detailLine}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-1">
        <div
          className={`rounded-full px-3 py-1 text-xs font-bold ${
            active
              ? "bg-emerald-50 text-emerald-700"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          {activeLabel}
        </div>
        {packetTimestamp && (
          <span className="text-xs font-semibold text-slate-400">
            {formatTimestamp(packetTimestamp)}
          </span>
        )}
      </div>
    </div>
  );
};

const NoLiveDataNotice = () => (
  <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
    No live data: device is not reporting this pump
  </p>
);

const ControllerCardFooter = ({ battery, healthText, healthy }) => (
  <div className="flex items-center gap-5 border-t border-slate-100 px-3 py-2.5 text-xs sm:px-4">
    <span className="flex items-center gap-2 font-semibold text-slate-600">
      <Battery className="h-3.5 w-3.5" />
      {formatWholePercent(battery)}%
    </span>
    <span
      className={`flex items-center gap-2 font-semibold ${
        healthy ? "text-emerald-500" : "text-red-500"
      }`}
    >
      <Heart className="h-3.5 w-3.5" />
      {healthText}
    </span>
  </div>
);

const EmptyCardMessage = ({ message }) => (
  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-8 text-center text-sm font-medium text-slate-500">
    {message}
  </div>
);

const TankPanel = ({
  tank,
  tower,
  mqttConnected,
  pendingCommands,
  publishCommand,
  publishValveToggleCommand,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
}) => {
  const levelDeviceId = getUsableReference(
    tank.level_controller_device_id,
    tower.level_controller?.device_id,
  );
  const levelController =
    getControllerByDeviceId(
      tower,
      "level_controllers",
      "level_controller",
      levelDeviceId,
    ) || tower.level_controller;
  const pumpDeviceId = getUsableReference(
    tank.pump_controller_device_id,
    tower.pump_controller?.device_id,
  );
  const pumpController =
    getControllerByDeviceId(
      tower,
      "pump_controllers",
      "pump_controller",
      pumpDeviceId,
    ) || tower.pump_controller;
  const valveId = getUsableReference(tank.valve_id);
  const valveDisplayId = getUsableReference(
    tank.valve_custom_id,
    tank.valve_id,
  );
  const pumpPacket = tank.pump_packet;
  const pumpPacketId = getUsableReference(pumpPacket?.pump_id);
  const pumpId =
    pumpPacketId ||
    getUsableReference(tank.pump?.pump_custom_id, tank.pump?.pump_id);
  const percentage = formatWholePercent(tank.tank_percentage);
  const tankHigh = Number(tank.high);
  const isTankAboveHigh =
    typeof percentage === "number" &&
    Number.isFinite(tankHigh) &&
    percentage > tankHigh;
  const valveState = getUsableReference(tank.valve_state);
  const pumpState = getUsableReference(pumpPacket?.pump_state);
  const rawValveOn = isOn(tank.valve_state);
  const rawPumpOn = isOn(pumpState);
  const levelOperationMode = normalize(
    tank.level_controller_mode || levelController?.operation_mode,
  );
  const levelRemoteMode = normalize(
    tank.level_controller_remote || levelController?.remote_mode,
  );
  const pumpOperationMode = normalize(
    pumpPacket?.atbc_mode || pumpController?.operation_mode,
  );
  const pumpRemoteMode = normalize(
    pumpPacket?.atbc_remote || pumpController?.remote_mode,
  );
  const hasLevelControllerPacket = Boolean(levelController && levelDeviceId);
  const hasValveCommandTarget = Boolean(valveId);
  const hasPumpControllerPacket = Boolean(pumpController && pumpDeviceId);
  const hasPumpPacket = Boolean(pumpPacket && pumpPacketId);
  const hasValveStatus = Boolean(
    hasLevelControllerPacket && hasValveCommandTarget && valveState,
  );
  const hasPumpStatus = Boolean(
    hasPumpControllerPacket && hasPumpPacket && pumpState,
  );
  const valveOn = hasValveStatus && rawValveOn && !isTankAboveHigh;
  const pumpOn = hasPumpStatus && rawPumpOn;
  const canControlValve = Boolean(
    hasLevelControllerPacket &&
    hasValveCommandTarget &&
    mqttConnected &&
    levelRemoteMode === "on" &&
    (valveOn || !isTankAboveHigh),
  );
  const canSendValveRemote = Boolean(
    hasLevelControllerPacket && hasValveCommandTarget && mqttConnected,
  );
  const canControlPump = Boolean(
    hasPumpControllerPacket &&
    hasPumpPacket &&
    mqttConnected &&
    isOn(pumpRemoteMode),
  );
  const canSendPumpRemote = Boolean(
    hasPumpControllerPacket && hasPumpPacket && mqttConnected,
  );
  const pendingCommandList = Object.values(pendingCommands);
  const valvePending = pendingCommandList.some(
    (command) =>
      (command.type === "valve" &&
        command.deviceId === levelDeviceId &&
        command.targetId === valveId) ||
      (command.type === "remote" &&
        command.controllerType === "level" &&
        command.deviceId === levelDeviceId),
  );
  const pumpPending = pendingCommandList.some(
    (command) =>
      (command.type === "pump" &&
        command.deviceId === pumpDeviceId &&
        command.targetId === pumpPacketId) ||
      (command.type === "remote" &&
        command.controllerType === "pump" &&
        command.deviceId === pumpDeviceId),
  );

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-[82px_1fr_auto] md:items-center">
        <div className="flex justify-center md:block">
          <TankGauge
            percentage={percentage}
            timestamp={
              tank.level_controller_timestamp || levelController?.timestamp
            }
          />
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="truncate text-lg font-bold text-slate-950">
              {tank.tank_name || tank.tank_custom_id || tank.tank_id}
            </h3>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-400">
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {levelDeviceId || "-"}
          </div>
          <StatusLine
            active={valveOn}
            label={
              hasValveStatus ? `Valve ${valveOn ? "ON" : "OFF"}` : "Valve N/A"
            }
            timestamp={
              tank.level_controller_timestamp || levelController?.timestamp
            }
          />
          <StatusLine
            active={pumpOn}
            label={hasPumpStatus ? `Pump ${pumpOn ? "ON" : "OFF"}` : "Pump N/A"}
            timestamp={pumpController?.timestamp}
          />
        </div>

        <div className="hidden rounded-full bg-red-50 p-3 text-red-500 md:block">
          <Droplet className="h-5 w-5 fill-current" />
        </div>
      </div>

      <ControlRow
        type="valve"
        title={formatControlTitle("Valve", valveDisplayId)}
        icon={<SlidersHorizontal className="h-5 w-5" />}
        mode={levelOperationMode}
        remoteMode={levelRemoteMode}
        checked={valveOn}
        disabled={!canControlValve}
        remoteDisabled={!canSendValveRemote}
        pending={valvePending}
        onRemoteToggle={(enabled) => {
          publishRemoteOperationCommand({
            societyId: tower.society_id,
            deviceId: levelDeviceId,
            controllerType: "level",
            remoteOperations: enabled ? "on" : "off",
          });
        }}
        onToggle={(checked) =>
          publishValveToggleCommand({
            societyId: tower.society_id,
            deviceId: levelDeviceId,
            valveId,
            operation: checked ? "open" : "close",
          })
        }
      />

      <ControlRow
        type="pump"
        title={formatControlTitle("Pump", pumpId)}
        icon={<Power className="h-5 w-5" />}
        mode={hasPumpControllerPacket && hasPumpPacket ? pumpOperationMode : ""}
        remoteMode={
          hasPumpControllerPacket && hasPumpPacket ? pumpRemoteMode : ""
        }
        ugtId={pumpPacket?.ugt_id}
        ugtLevel={pumpPacket?.ugt_level}
        checked={pumpOn}
        disabled={!canControlPump}
        remoteDisabled={!canSendPumpRemote}
        pending={pumpPending}
        onRemoteToggle={(enabled) => {
          publishRemoteOperationCommand({
            societyId: tower.society_id,
            deviceId: pumpDeviceId,
            controllerType: "pump",
            remoteOperations: enabled ? "on" : "off",
          });
        }}
        onToggle={(checked) =>
          publishPumpToggleCommand({
            societyId: tower.society_id,
            deviceId: pumpDeviceId,
            pumpId: pumpPacketId,
            operation: checked ? "on" : "off",
          })
        }
      />
    </div>
  );
};

const TankGauge = ({ percentage, timestamp, showTimestamp = true }) => {
  const fillHeight =
    typeof percentage === "number" ? Math.max(0, Math.min(100, percentage)) : 0;

  return (
    <div className="w-[82px] text-center">
      <div className="relative mx-auto h-[86px] w-[64px] overflow-hidden rounded-[18px] border-2 border-slate-200 bg-slate-50 shadow-inner">
        <div
          className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-500 to-blue-200 transition-all"
          style={{ height: `${fillHeight}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-center text-lg font-extrabold text-slate-800">
          {typeof percentage === "number" ? `${percentage}%` : "-"}
        </div>
      </div>
      {showTimestamp && (
        <p className="mt-1.5 text-[10px] font-semibold text-slate-400">
          {formatTimestamp(timestamp)}
        </p>
      )}
    </div>
  );
};

const StatusLine = ({ active, label, timestamp }) => (
  <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-3 text-xs">
    <span className="flex items-center gap-2 font-semibold text-slate-600">
      <span
        className={`h-2.5 w-2.5 rounded-full ${active ? "bg-green-500" : "bg-slate-300"}`}
      />
      {label}
    </span>
    <span className="text-slate-400">{formatTimestamp(timestamp)}</span>
  </div>
);

const ControlRow = ({
  type,
  title,
  icon,
  mode,
  remoteMode,
  ugtId,
  ugtLevel,
  checked,
  disabled,
  remoteDisabled,
  showUgtLevel = type === "pump",
  pending,
  onRemoteToggle,
  onToggle,
}) => {
  const isSwitchDisabled = disabled || pending;
  const isRemoteDisabled = remoteDisabled || pending;
  const availableModeOptions =
    type === "valve"
      ? modeOptions.filter((option) => option !== "manual")
      : modeOptions;
  const normalizedMode = normalize(mode);
  const activeMode = isOn(remoteMode)
    ? "remote"
    : type === "valve"
      ? "auto"
      : normalizedMode;
  const noAccess = disabled && remoteDisabled;
  const canChangeRemoteMode = !isRemoteDisabled;
  const iconClass = disabled
    ? "bg-slate-200 text-slate-400"
    : type === "valve"
      ? "bg-orange-400 text-white"
      : "bg-blue-500 text-white";
  const titleClass = disabled ? "text-slate-500" : "text-slate-900";
  const ugtBadgeClass = disabled
    ? "border-slate-200 bg-slate-100 text-slate-400"
    : "border-blue-100 bg-white text-slate-600 shadow-sm";

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between ${
        disabled
          ? "border-slate-100 bg-slate-100/70"
          : "border-slate-100 bg-slate-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconClass}`}
        >
          {icon}
        </div>
        <div>
          <h4 className={`text-sm font-bold ${titleClass}`}>{title}</h4>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {availableModeOptions.map((option) => (
              <button
                key={option}
                type="button"
                disabled={
                  !canChangeRemoteMode ||
                  (option !== "auto" && option !== "remote") ||
                  activeMode === option
                }
                onClick={() => {
                  if (option === "remote") {
                    onRemoteToggle(true);
                  } else if (option === "auto") {
                    onRemoteToggle(false);
                  }
                }}
                className={`rounded-md border px-2.5 py-0.5 text-[11px] font-semibold capitalize transition ${
                  type === "valve"
                    ? activeMode === option
                      ? "border-sky-300 bg-sky-100 text-sky-900"
                      : "border-slate-200 bg-white text-slate-300 opacity-60"
                    : noAccess
                    ? activeMode === option
                      ? "border-slate-300 bg-slate-200/70 text-slate-500"
                      : "border-slate-200 bg-slate-100/50 text-slate-300"
                    : activeMode === option
                      ? "border-cyan-300 bg-cyan-50 text-slate-900"
                      : "border-slate-200 bg-white text-slate-400"
                } ${
                  canChangeRemoteMode &&
                  (option === "auto" || option === "remote") &&
                  activeMode !== option
                    ? "cursor-pointer hover:border-cyan-200 hover:text-slate-700"
                    : "cursor-default pointer-events-none"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          {showUgtLevel && (
            <div
              className={`mt-2 inline-flex items-center gap-2 rounded-lg border px-2.5 py-1 text-xs font-semibold ${ugtBadgeClass}`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-md ${
                  disabled
                    ? "bg-slate-200 text-slate-400"
                    : "bg-blue-50 text-blue-600"
                }`}
              >
                <Waves className="h-3.5 w-3.5" />
              </span>
              <span className={disabled ? "text-slate-400" : "text-slate-500"}>
                UGT {ugtId && ugtId !== "-" ? ugtId : "Level"}
              </span>
              <span className={disabled ? "text-slate-400" : "text-blue-700"}>
                {formatUgtLevel(ugtLevel)}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 sm:justify-end">
        {pending && (
          <span className="flex items-center gap-1.5 text-xs font-semibold text-cyan-700">
            <Loader className="h-3.5 w-3.5 animate-spin" />
            Processing...
          </span>
        )}
        <Switch
          checked={checked}
          disabled={isSwitchDisabled}
          onCheckedChange={onToggle}
        />
      </div>
    </div>
  );
};

export default Dashboard;
